import { GameRenderer } from './engine/renderer';
import { CameraSystem } from './engine/camera';
import { GameScene } from './engine/scene';
import { AssetLoader } from './engine/loaders';
import { InputManager, getEffectiveTouchLookRate, type InputState } from './input/input-manager';
import { TouchControls } from './input/touch-controls';
import { MobileStartupOverlay, isLikelyMobile, isCapacitorNative, isElectronDesktop } from './input/mobile';
import { NetworkClient } from './network/client';
import {
  captureAttributionFromUrl,
  clearStoredAttribution,
  getStoredAttribution,
} from './network/attribution';
import {
  initRevenueCat,
  identifyRevenueCatUser,
  resetRevenueCatUser,
  onProStatusChanged,
  proStatusFrom,
  type ProStatus,
} from './network/revenuecat';
import { GameState, type EntityDamageEffectEvent } from './game/state';
import { EntityRenderer } from './game/entity-renderer';
import { SurfaceRenderer } from './procedural/surface-renderer';
import { HUD } from './ui/hud';
import { AudioManager } from './audio/audio-manager';
import { ControlsHelp } from './ui/controls-help';
import { AuthScreen, type AuthResult } from './ui/auth-screen';
import { loadPersistedLocale } from './ui/locale-storage';
import { armBeforeInstallPromptCapture } from './ui/install-prompt';
import { CharCreateScreen, type CharacterData } from './ui/char-create';
import { CharSelectScreen, type SavedCharacter } from './ui/char-select';
import { Tutorial } from './ui/tutorial';
import { PauseMenu, type PauseAction } from './ui/pause-menu';
import { UpgradeModal } from './ui/upgrade-modal';
import { WorldLabels } from './ui/world-labels';
import { StationUI, type StationData, type TerritoryPanelData, type TerritoryRaceCard, type TerritoryStatus } from './ui/station-ui';
import { WarpGateUI } from './ui/warp-gate-ui';
import { UniverseMap } from './ui/universe-map';
import { LeaderboardUI } from './ui/leaderboard';
import { EffectsManager } from './game/effects';
import { InventoryPanel } from './ui/inventory-panel';
import { MissionJournal } from './ui/mission-journal';
import { FriendsPanel } from './ui/friends-panel';
import { ChatWindow } from './ui/chat-window';
import { MarketplacePanel } from './ui/marketplace-panel';
import { Chat } from './ui/chat';
import { PerfMonitor, formatPerfLine } from './ui/perf-monitor';
import * as THREE from 'three';
import {
  setLocale, getLocale, type Locale,
  loadLocale,
  MessageType, RESOURCE_INFO, ResourceTier, EntityType,
  WARP_GATE_DISTANCE, SURFACE_LAND_DISTANCE, sectorKey, sectorName,
  collectFloraColliders,
  WEAPONS_ORDERED, SHIELDS_ORDERED, ENGINES_ORDERED,
  CONSUMABLES_ORDERED,
  WEAPONS, SHIELDS, ENGINES, CONSUMABLES,
  t,
  itemLabel,
  itemDescription,
  ChatChannel,
  type ChatReceiveMsg,
  type ChatHistoryDataMsg,
  type PlayerProfileDataMsg,
  type ErrorMsg,
  type HitConfirmMsg, type ResourceCollectedMsg, type InventoryUpdateMsg,
  type TutorialPhaseMsg,
  type DamageEventMsg, type SectorDiscoveredMsg,
  type SectorChangeMsg,
  type LandResultMsg,
  type LiftoffResultMsg,
  type SurfaceDataMsg,
  type InvestigateRuinResultMsg,
  type CodexUnlockMsg,
  type SurfaceWeatherMsg,
  type PlanetReputationUpdateMsg,
  type LiberatedSurfacesMsg,
  type WarpTravelResultMsg, type SectorCoord,
  type RespawnConfirmMsg, type DeathEventMsg,
  type ActiveBuffsUpdateMsg,
  type MerchantOfferMsg,
  type SystemAnnouncementMsg,
  type LeaderboardDataMsg,
  type StationShopSnapshotMsg,
  type MissionListMsg,
  type MissionCompleteMsg,
  type SectorClaim,
  LeaderboardType,
  dominantRace,
  getZoneDifficulty,
  CLAIM_COST_BY_DIFFICULTY,
  GARRISON_REINFORCE_COST_BY_LEVEL,
  GARRISON_LEVEL_MAX,
  GARRISON_NPCS_PER_LEVEL,
  OWNER_SHOP_DISCOUNT,
  OWNER_REVENUE_SHARE,
  OWNER_REVENUE_DAILY_CAP,
  SIEGE_WINDOW_MS,
  RACE_INFO,
  formatRaceTraitSummary,
  raceNameKey,
  raceDescriptionKey,
  RaceId,
  type SiegeUpdateMsg,
  type SectorClaimUpdateMsg,
  type NpcItemDropMsg,
  type FriendRequestReceivedMsg,
  type FriendStatusUpdateMsg,
  type TradeResponseMsg,
  type MarketplaceResultMsg,
  type MarketplaceSoldMsg,
  type PremiumUpdateMsg,
  type AccountDeletedMsg,
  type SavedCharacterMsg,
  type PlayerShipCosmetics,
  type ShipCosmeticsUpdateMsg,
  type ShipHangarUpdateMsg,
  SHIP_CLASS_KEY,
  SHIP_CLASS_VISUAL_SCALE,
  DEFAULT_SHIP_COSMETICS,
} from '@viral-space/shared';

/**
 * Idle window for the damage-received chat aggregator. After a hit
 * lands we wait this many ms for *the same attacker* to keep firing —
 * any new hit resets the timer, so a sustained burst collapses into a
 * single chat line instead of spamming the activity log per shot.
 */
const DAMAGE_AGG_IDLE_MS = 700;
/**
 * Hard ceiling for the aggregator. Even if an attacker keeps the
 * pressure on, we flush the running total at most every
 * {@link DAMAGE_AGG_MAX_MS} so a long, continuous beam still surfaces
 * in real time instead of waiting for the player to die or break the
 * line of sight first.
 */
const DAMAGE_AGG_MAX_MS = 2500;

class ViralSpace {
  private renderer!: GameRenderer;
  private camera!: CameraSystem;
  private scene!: GameScene;
  private assets!: AssetLoader;
  private input!: InputManager;
  private network!: NetworkClient;
  private state!: GameState;
  private entityRenderer!: EntityRenderer;
  private surfaceRenderer!: SurfaceRenderer;
  /**
   * `true` while the player is on a planetary surface (between a
   * successful `LandResult` and the next `LiftoffResult`). Drives the
   * camera world-up flag, the LAND/LIFTOFF HUD prompt, and the
   * surface-aware `LandRequest` message router.
   */
  private onSurface = false;
  private hud!: HUD;
  private worldLabels!: WorldLabels;
  private stationUI!: StationUI;
  private warpGateUI!: WarpGateUI;
  private universeMap!: UniverseMap;
  private leaderboard!: LeaderboardUI;
  private effects!: EffectsManager;
  private inventoryPanel!: InventoryPanel;
  private missionJournal!: MissionJournal;
  private friendsPanel!: FriendsPanel;
  private chatWindow!: ChatWindow;
  private marketplacePanel!: MarketplacePanel;
  private chat!: Chat;
  private docked = false;
  private dockSectorKey: string | null = null;
  private dockStationId: string | null = null;
  // Surface-ruin hold-to-investigate state (Phase 2.1). The progress
  // accumulates while KeyE is held inside a ruin's radius; releasing
  // KeyE or stepping out of range resets it.
  private _keyEHeld = false;
  private _ruinInvestigateMs = 0;
  private _ruinInvestigateSiteId: string | null = null;
  // Codex fragment ids the pilot has unlocked (Phase 2.3). Hydrated
  // from the server's initial `CodexUnlock` snapshot after login;
  // grows monotonically as the pilot investigates ruins.
  private _unlockedCodex = new Set<string>();
  // Per-planet reputation snapshot (Phase 3.2). Server-pushed; the
  // dock UI reads it via `stationUI.getPlanetReputation`.
  private _planetReputation = new Map<string, number>();
  // Liberated surface ids (Phase 3.3). Cosmetic badge driver.
  private _liberatedSurfaces = new Set<string>();
  private static readonly RUIN_INVESTIGATE_DURATION_MS_CONST = 3000;
  private activeMerchantId: string | null = null;
  private merchantModalEl: HTMLElement | null = null;
  private pendingWarpGateId: string | null = null;
  /**
   * Most recent station/merchant BUY request that hasn't been ack'd yet.
   * The server only confirms a successful purchase via the next
   * `InventoryUpdate`, so we stash the item label + price here at click
   * time and use it to render the post-purchase HUD notification (and
   * play the cash-register audio) when the inventory message arrives.
   * On `buy_failed:` errors we instead play the error sound and surface
   * the reason. Cleared on undock or after a short timeout to avoid
   * stale state if the server never replies.
   */
  private pendingBuy: {
    itemId: string;
    name: string;
    price: number;
    expiresAt: number;
  } | null = null;
  /**
   * Same pattern as `pendingBuy`: we remember the last CRAFT click so the
   * next `InventoryUpdate` can show a localized confirmation toast. Cleared
   * on `craft_failed:` or undock.
   */
  private pendingCraft: {
    recipeId: string;
    name: string;
    expiresAt: number;
  } | null = null;
  private audio!: AudioManager;
  private controls!: ControlsHelp;
  private tutorial!: Tutorial;
  private pauseMenu!: PauseMenu;

  private authScreen!: AuthScreen;
  private charCreate!: CharCreateScreen | null;
  private charSelect!: CharSelectScreen | null;
  private serverCharacters: SavedCharacter[] = [];

  /**
   * Reusable Three.js scratch objects fed into `CameraSystem.update`
   * every frame. Allocating fresh `Vector3 / Quaternion / Euler`
   * instances per frame (the previous code did) produced ~360 GC-
   * tracked Three.js objects per second, contributing to the
   * "freezes when I move" stutters reported on planetary surfaces.
   * Reusing the same instances keeps the per-frame allocation count
   * for the camera path at zero.
   */
  private _camTargetVec = new THREE.Vector3();
  private _camTargetQuat = new THREE.Quaternion();
  private _camTargetEuler = new THREE.Euler(0, 0, 0, 'YXZ');
  private _camTargetIO: { position: THREE.Vector3; quaternion: THREE.Quaternion } = {
    position: this._camTargetVec,
    quaternion: this._camTargetQuat,
  };

  private touchControls: TouchControls | null = null;
  private mobileOverlay: MobileStartupOverlay | null = null;
  private isMobile = false;

  private currentUser: AuthResult | null = null;
  /**
   * Live premium snapshot from the most recent `AuthResponse`. Used by
   * the character-creation screen to grey out the premium-only race
   * cards (and surface a "Premium" badge) for free accounts. Server is
   * still the authority — this only drives presentation.
   */
  private premium: { active: boolean; currentPeriodEnd: number | null } = {
    active: false, currentPeriodEnd: null,
  };
  /**
   * Per-pilot ship cosmetics (pattern + colors). Populated from the
   * `ShipHangarUpdate` and `ShipCosmeticsUpdate` messages, mirrored
   * locally on optimistic Apply from the Customize modal so the
   * StationUI sees the new bundle on the very next refresh without
   * waiting for the round-trip. Server is the authority — its echoed
   * `ShipCosmeticsUpdate` overwrites this if the request was clamped.
   */
  private shipCosmetics: PlayerShipCosmetics = { ...DEFAULT_SHIP_COSMETICS };
  /**
   * Live admin flag from the most recent `AuthResponse`. Drives admin-only
   * client surfaces (the in-game perf / memory monitor, future debug
   * overlays). The server is still the authority for every privileged
   * action — this only enables presentation that's safe to leak.
   */
  private isAdmin = false;
  /**
   * Lazy admin-only memory + FPS monitor. Constructed on first admin
   * login, then started whenever chat is ready and stopped on sign-out
   * or disconnect so a non-admin tab never accumulates "[perf]" lines
   * in its devtools console.
   */
  private perfMonitor: PerfMonitor | null = null;
  /**
   * Server-issued opaque session id from the most recent successful
   * `AuthResponse`. Doubles as the `x-billing-token` bearer for the
   * `/api/billing/*` HTTP endpoints (Stripe checkout / portal). `null`
   * before login and after sign-out so the upgrade UI can no-op rather
   * than firing requests with an empty header.
   */
  private billingToken: string | null = null;
  /**
   * Lazily-built premium upgrade modal. Shared between the
   * pilot-selection "UNLOCK PREMIUM" CTA and the in-game pause menu so
   * a single instance receives every `PremiumUpdate` push, regardless
   * of which surface opened it.
   */
  private upgradeModal: UpgradeModal | null = null;
  private currentCharacter: SavedCharacter | null = null;
  private lastTime = 0;
  private lastWeaponSoundAt = 0;
  /**
   * Projectile entity ids whose spawn the client has already observed.
   * Used to detect the point-blank case where a hit lands inside the same
   * server tick as the shot — the projectile never reaches the client and
   * its id will be missing from this set, so we know to play the weapon
   * sound from the `HitConfirm` path instead.
   */
  private seenProjectileIds = new Set<string>();
  private running = false;
  private paused = false;
  /**
   * Set by the {@link MessageType.Error} handler when the server
   * announces an involuntary close it wants the client to surface
   * specifically (e.g. `kicked:other_login` after the same account logs
   * in from another device). Consumed by {@link handleServerDisconnect}
   * so the auth-screen banner says *why* we were dropped instead of
   * defaulting to the generic "Disconnected" copy. Cleared after each
   * use so a later, unrelated drop never reads stale state.
   */
  private pendingDisconnectReason: 'kicked' | null = null;
  private connectErrorEl: HTMLElement | null = null;
  private debugEl: HTMLElement | null = null;
  private debugVisible = false;
  /** Last wire `SiegeUpdate.phase` per sector (for transition FX). */
  private lastSiegePhaseBySector = new Map<string, string>();
  /** Unsub from `GameState.onEntityDamageEffect` while in space. */
  private damageEffectUnsub: (() => void) | null = null;
  /**
   * Per-target time of the last per-hit VFX. Used so merged HP-delta
   * bursts do not stack on top of a live `HitConfirm` stream, while still
   * covering the case where `HitConfirm` is missing and many shots apply
   * in one `EntityUpdate`.
   */
  private lastHitConfirmVfxAt = new Map<string, number>();

  /**
   * Per-attacker rolling sum of damage taken by the local player. We
   * aggregate over a short idle window so a fast-firing weapon (e.g. a
   * pulse blaster at 8 shots/sec) collapses into a single chat line
   * "Hit by Pirate Fighter for 56 dmg (8× hits)" instead of spamming
   * the activity log eight times. Each entry holds a single trailing
   * timer that flushes once no new hits have arrived for
   * {@link DAMAGE_AGG_IDLE_MS} ms, plus a {@link DAMAGE_AGG_MAX_MS}
   * hard cap so a sustained barrage still surfaces in real time.
   */
  private damageReceivedAgg = new Map<
    string,
    {
      attackerId: string;
      attackerLabel: string;
      hullTotal: number;
      shieldTotal: number;
      hits: number;
      firstAt: number;
      idleTimer: ReturnType<typeof setTimeout>;
      maxTimer: ReturnType<typeof setTimeout>;
    }
  >();

  async init() {
    this.setLoadProgress(10);

    // Snapshot any UTM / ad-click tags from the URL into localStorage
    // before anything else happens so a slow asset load can't lose
    // them to a tab focus loss or a full-screen handoff. The snapshot
    // is first-touch — re-arriving with a different campaign won't
    // overwrite an existing capture (see attribution.ts) — and the
    // server only persists it on signup.
    captureAttributionFromUrl();

    // Restore saved display prefs (fullscreen + windowed resolution) when
    // running inside the Electron / Steam desktop shell. We apply them as
    // early as possible — before assets load — so the window settles into
    // the correct size before the player sees the loading screen.
    if (isElectronDesktop()) {
      const winBridge = (window as Window & typeof globalThis).zerionDesktop?.window;
      if (winBridge) {
        try {
          const raw = localStorage.getItem('zerion:displayPrefs');
          if (raw) {
            const prefs = JSON.parse(raw) as { fullscreen?: boolean; resolution?: [number, number] | null };
            if (typeof prefs.fullscreen === 'boolean') {
              winBridge.setFullscreen(prefs.fullscreen).catch(() => { /* ignore */ });
            }
            if (!prefs.fullscreen && Array.isArray(prefs.resolution) && prefs.resolution.length === 2) {
              winBridge.setResolution(prefs.resolution[0], prefs.resolution[1]).catch(() => { /* ignore */ });
            }
          }
        } catch { /* ignore — corrupted prefs don't break the game */ }
      }
    }

    // Capture the Android Chrome `beforeinstallprompt` event as early
    // as possible — the browser only fires it once per page load, and
    // missing it means the install modal can't offer the one-tap
    // native install button. Idempotent and a no-op outside Chromium.
    armBeforeInstallPromptCapture();

    // Boot the RevenueCat SDK on the Capacitor (iOS / Android) shells
    // as early as possible so pending store transactions (Ask-to-Buy
    // approvals, offer codes) are processed even before sign-in. The
    // customer-info listener is the push channel that flips the local
    // premium flag the instant a paywall purchase / renewal / expiry
    // lands — no polling. Both calls no-op outside the native shells
    // and never reject, so this cannot break web/desktop boot.
    void initRevenueCat().then((ok) => {
      if (!ok) return;
      void onProStatusChanged((status) => this.applyNativeProStatus(status));
    });

    const container = document.getElementById('game-container')!;

    this.isMobile = isLikelyMobile();

    this.renderer = new GameRenderer();
    // Wire the post-resize hook now (before the first orientation event
    // can fire) so the camera's projection matrix tracks any future
    // device rotation. Without this the canvas would resize but the
    // camera kept the launch-time aspect, leaving the scene visibly
    // stretched after the first rotation on mobile / tablet.
    this.renderer.init(container, (w, h) => {
      this.camera?.setAspect(w / h);
    });
    this.setLoadProgress(20);

    this.camera = new CameraSystem(
      container.clientWidth / container.clientHeight,
    );
    this.scene = new GameScene();
    this.setLoadProgress(30);

    this.assets = new AssetLoader();
    this.setLoadProgress(40);

    this.renderer.setupPostProcessing(
      this.scene.getScene(),
      this.camera.getCamera(),
    );
    this.setLoadProgress(50);

    this.input = new InputManager(container);

    // Open the pause menu on the very first ESC press, even when
    // pointer lock is currently active. Browsers (Chrome especially)
    // swallow the ESC keydown that would otherwise trigger the
    // window-level handler below — they consume that keystroke for
    // their built-in pointer-lock exit. Without this hook the player
    // had to press ESC twice (once to unlock, once to open the menu).
    // The InputManager only fires this when the lock was lost while
    // input was still enabled, so opening the inventory / map / etc.
    // (which calls `input.disable()` first) won't double-trigger.
    this.input.setPointerLockExitCallback(() => this.handlePointerLockEscape());

    if (this.isMobile) {
      // Touch overlay is rendered inside `game-container` above the canvas
      // but below `#hud`. It owns all touch input while mobile is on;
      // see `InputManager.setTouchSource`.
      this.touchControls = new TouchControls(container);
      this.input.setTouchSource(this.touchControls);
      this.wireTouchCallbacks();
    }

    this.setLoadProgress(55);

    this.state = new GameState();
    this.entityRenderer = new EntityRenderer(this.scene, this.assets);
    this.surfaceRenderer = new SurfaceRenderer(this.scene.getScene());
    this.setLoadProgress(60);

    this.audio = new AudioManager();
    this.setLoadProgress(70);

    const hudContainer = document.getElementById('hud')!;

    this.hud = new HUD(hudContainer, { isMobile: this.isMobile });
    this.hud.setPremium(this.premium.active);
    this.worldLabels = new WorldLabels(hudContainer);
    this.stationUI = new StationUI(hudContainer);
    this.stationUI.onUndock = () => this.undockFromStation();
    this.stationUI.onSell = (itemId, qty) => this.network.sendSellItem(itemId, qty);
    this.stationUI.onUpgradeClick = (source) => {
      this.openUpgradeModal(source);
    };
    this.stationUI.onBuy = (itemId, info) => {
      // Record the in-flight purchase so the InventoryUpdate / Error
      // handler can surface a localized confirmation. 4-second TTL is
      // generous enough for satellite-grade roundtrips but still expires
      // a stuck request before the user opens a different menu.
      this.pendingBuy = {
        itemId,
        name: info.name,
        price: info.price,
        expiresAt: performance.now() + 4000,
      };
      this.audio.playUI('select');
      this.network.sendBuyItem(itemId, 1);
    };
    this.stationUI.onCraft = (recipeId, displayName) => {
      this.pendingCraft = {
        recipeId,
        name: displayName,
        expiresAt: performance.now() + 4000,
      };
      this.network.sendCraftItem(recipeId);
      this.audio.playUI('select');
    };
    this.stationUI.onBuyShip = (shipClass) => this.handleBuyShip(shipClass);
    this.stationUI.onSwitchShip = (shipClass) => this.handleSwitchShip(shipClass);
    this.stationUI.getPlayerResources = () => this.buildPlayerResourceSnapshot();
    this.stationUI.getPlayerLevel = () => this.state.getPlayerState().level ?? 1;
    this.stationUI.getEquipment = () => this.state.getEquipment();
    this.stationUI.getUnlockedCodex = () => this._unlockedCodex;
    this.stationUI.getPlanetReputation = (surfaceId: string) =>
      this._planetReputation.get(surfaceId) ?? 0;
    this.stationUI.getLiberatedSurfaces = () => this._liberatedSurfaces;
    this.stationUI.getHangar = () => ({
      owned: this.state.getPlayerState().ownedShipClasses.slice(),
      active: this.state.getPlayerState().shipClass ?? 0,
      credits: this.state.getPlayerState().credits ?? 0,
      level: this.state.getPlayerState().level ?? 1,
      raceId: this.state.getPlayerState().raceId,
      cosmetics: this.shipCosmetics,
    });
    this.stationUI.onSetShipCosmetics = (cos) => {
      // Mirror the request locally so the next StationUI.refresh()
      // shows the new bundle without waiting for the
      // ShipCosmeticsUpdate roundtrip. The server's authoritative
      // response will replace this if it gets clamped.
      this.shipCosmetics = { ...cos };
      this.network.sendSetShipCosmetics(cos);
    };
    this.stationUI.onClaimSector = (sk) => this.network.sendClaimSector(sk);
    this.stationUI.onReinforceGarrison = (sk, lvl) =>
      this.network.sendReinforceGarrison(sk, lvl);
    this.stationUI.onMissionAccept = (missionId) => {
      this.network.sendMissionAccept(missionId);
      this.audio.playUI('select');
    };
    this.stationUI.onMissionAbandon = (missionId) => {
      this.network.sendMissionAbandon(missionId);
      this.audio.playUI('error');
    };
    this.stationUI.getActiveMissionIds = () =>
      new Set(this.state.getActiveMissions().map((m) => m.id));
    this.stationUI.getActiveMissions = () => this.state.getActiveMissions();
    this.stationUI.isPremium = () => this.premium.active;
    this.stationUI.getBountyContracts = () => ({
      contracts: this.state.getBountyContracts(),
      serverTime: this.state.getBountyServerTime(),
    });
    this.warpGateUI = new WarpGateUI(hudContainer, {
      onTravel: (dest) => this.requestWarpTravel(dest),
      onClose: () => this.closeWarpGateUI(),
      onUpgrade: () => {
        this.closeWarpGateUI();
        this.openUpgradeModal('warp-gate');
      },
    });
    this.universeMap = new UniverseMap(hudContainer, {
      onSetDestination: (dest) => {
        this.state.setNavigationTarget(dest);
      },
      onClose: () => this.closeUniverseMap(),
    });
    this.leaderboard = new LeaderboardUI(hudContainer, {
      onRequest: (board) => this.network.sendLeaderboardRequest(board, 50),
      onClose: () => this.closeLeaderboard(),
    });
    this.effects = new EffectsManager(this.scene.getScene());
    this.inventoryPanel = new InventoryPanel(hudContainer);
    this.inventoryPanel.getEquipment = () => this.state.getEquipment();
    // Credits can change via several paths (warp payments, mission
    // rewards, sales) that don't emit a fresh `InventoryUpdate`. Pulling
    // from the authoritative player state at render time keeps the
    // inventory header in lockstep with the HUD counter.
    this.inventoryPanel.getCredits = () => this.state.getPlayerState().credits ?? 0;
    this.inventoryPanel.onEquip = (slot, itemId) => {
      this.network.sendEquipItem(slot, itemId);
    };
    this.inventoryPanel.onUse = (itemId) => {
      this.network.sendUseItem(itemId);
    };
    // Re-enable flight input when the panel closes itself (mobile ✕
    // button). Keyboard / touch-callback open paths already pair their
    // own `input.disable()` with a matching `input.enable()`, but the ✕
    // button bypasses those and would otherwise leave input disabled —
    // on mobile that looked like "the ship won't move anymore after I
    // close the inventory."
    this.inventoryPanel.onClose = () => {
      if (this.running && !this.paused && !this.docked) {
        this.input.enable();
      }
    };

    this.missionJournal = new MissionJournal(hudContainer);
    this.missionJournal.getMissions = () => this.state.getActiveMissions();
    this.missionJournal.getCompletedMissions = () => this.state.getCompletedMissions();
    this.missionJournal.isPremium = () => this.premium.active;
    this.missionJournal.onAbandon = (missionId) => {
      this.network.sendMissionAbandon(missionId);
      this.audio.playUI('error');
    };
    this.missionJournal.getRadarMissionId = () => this.state.getRadarMissionId();
    this.missionJournal.onShowInRadar = (missionId, sector) => {
      if (sector === null) {
        // Toggling off the currently-pinned mission clears the radar
        // entirely. We don't try to fall back to "the previous mission"
        // because the radar is single-slot — un-pinning means the
        // pilot is done with that target.
        this.state.setNavigationTarget(null);
        this.hud.showNotification(
          t('mission.hiddenFromRadar'),
          0x00ff41,
        );
        this.audio.playUI('select');
        return;
      }
      // Pinning a mission overwrites whatever was on the radar before
      // (manual destination or another mission). The state layer
      // enforces the single-mission-at-a-time contract by storing only
      // one radarMissionId.
      this.state.setNavigationTarget(sector, missionId);
      this.hud.showNotification(
        t('mission.shownInRadar') + ` → [${sector.x}, ${sector.y}, ${sector.z}]`,
        0x00ff41,
      );
      this.audio.playUI('confirm');
    };
    this.missionJournal.onClose = () => {
      if (this.running && !this.paused && !this.docked) {
        this.input.enable();
      }
    };

    this.friendsPanel = new FriendsPanel(hudContainer);
    this.friendsPanel.getFriends = () => this.state.getFriends();
    this.friendsPanel.getIncomingRequests = () => this.state.getIncomingFriendRequests();
    this.friendsPanel.getOutgoingRequests = () => this.state.getOutgoingFriendRequests();
    this.friendsPanel.getInventory = () => ({
      items: this.state.getPlayerState().inventory.map((s) => ({
        itemId: s.itemId,
        quantity: s.quantity,
      })),
    });
    this.friendsPanel.callbacks = {
      onAdd: (name) => {
        this.network.sendFriendAdd(name);
        this.audio.playUI('select');
      },
      onAccept: (id) => {
        this.network.sendFriendAccept(id);
        this.audio.playUI('confirm');
      },
      onReject: (id) => {
        this.network.sendFriendReject(id);
        this.audio.playUI('error');
      },
      onRemove: (key) => {
        this.network.sendFriendRemove(key);
        this.audio.playUI('error');
      },
      onOpenDM: (name) => {
        // Open the DM thread in the full comms window so the pilot sees
        // the conversation history, not just a bare compose input.
        this.friendsPanel.hide();
        this.chatWindow.openDM(name);
      },
      onSendGift: (recipientName, itemId, qty) => {
        this.network.sendTradeRequest(recipientName, itemId, qty);
        this.audio.playUI('select');
      },
    };
    this.friendsPanel.onClose = () => {
      if (this.running && !this.paused && !this.docked) {
        this.input.enable();
      }
    };

    this.marketplacePanel = new MarketplacePanel();
    this.marketplacePanel.getListings = (scope) => this.state.getMarketplaceListings(scope);
    this.marketplacePanel.getServerTime = () => this.state.getMarketplaceServerTime();
    this.marketplacePanel.getInventory = () => ({
      items: this.state.getPlayerState().inventory.map((s) => ({
        itemId: s.itemId,
        quantity: s.quantity,
      })),
    });
    this.marketplacePanel.getCredits = () => this.state.getPlayerState().credits ?? 0;
    this.marketplacePanel.callbacks = {
      onRefresh: (scope) => this.network.sendMarketplaceList(scope),
      onCreate: (itemId, qty, price) => {
        this.network.sendMarketplaceCreate(itemId, qty, price);
        this.audio.playUI('select');
      },
      onCancel: (id) => {
        this.network.sendMarketplaceCancel(id);
        this.audio.playUI('error');
      },
      onBuy: (id) => {
        this.network.sendMarketplaceBuy(id);
        this.audio.playUI('select');
      },
    };
    // Marketplace renders inline inside the station's MARKETPLACE tab.
    // The station UI mounts/unmounts the panel as the user enters and
    // leaves the tab, so there's no separate modal layered on top of
    // the docked screen.
    this.stationUI.marketplaceView = this.marketplacePanel;

    this.chat = new Chat(hudContainer, { isMobile: this.isMobile });
    // All HUD "floating" pill notifications (XP gains, pickups, warps,
    // errors, etc.) are now logged to the chat transcript as activity
    // lines. This frees up the right edge of the screen and — more
    // importantly on mobile — gives the pilot a scrollable history of
    // recent events instead of text that disappears after 4 seconds.
    this.hud.onNotify = (text, color) => {
      this.chat.addActivity(text, color);
    };
    this.chat.onInputFocusChange = (focused) => {
      if (focused) {
        this.input.disable();
      } else if (
        this.running &&
        !this.paused &&
        !this.docked &&
        !this.inventoryPanel.visible &&
        !this.warpGateUI.isVisible() &&
        !this.universeMap.isVisible() &&
        !this.leaderboard?.isVisible() &&
        !this.tutorial?.isActive() &&
        !this.chatWindow.visible
      ) {
        this.input.enable();
      }
    };

    this.chatWindow = new ChatWindow(hudContainer, { isMobile: this.isMobile });
    this.chatWindow.callbacks = {
      sendMessage: (content, channel, targetName) => {
        if (channel === ChatChannel.Direct) {
          this.network.sendChat(channel, content, { targetName });
        } else {
          this.network.sendChat(channel, content);
        }
      },
      requestHistory: (channel, partnerName) => {
        this.network.sendChatHistoryRequest(channel, { partnerName, limit: 50 });
      },
      requestProfile: (name) => {
        this.network.sendPlayerProfileRequest(name);
      },
      onAddFriend: (name) => {
        this.network.sendFriendAdd(name);
        this.audio.playUI('select');
      },
      getFriends: () => this.state.getFriends(),
    };
    this.chatWindow.onOpenChange = (open) => {
      // The corner chatbox duplicates the transcript and pokes out from
      // behind the full comms window, so it yields the screen while the
      // window is up.
      if (open) {
        this.chat.hide();
      } else if (this.running) {
        this.chat.show();
      }
      if (open) {
        this.input.disable();
      } else if (
        this.running &&
        !this.paused &&
        !this.docked &&
        !this.inventoryPanel.visible &&
        !this.warpGateUI.isVisible() &&
        !this.universeMap.isVisible() &&
        !this.leaderboard?.isVisible() &&
        !this.tutorial?.isActive()
      ) {
        this.input.enable();
      }
    };
    this.chat.onExpand = () => {
      this.chatWindow.open();
      this.audio.playUI('select');
    };
    this.setLoadProgress(75);

    this.network = new NetworkClient(this.state);
    // Involuntary server disconnects (server crash, network drop, kick)
    // tear down the session and bounce the player back to the login
    // screen instead of silently looping reconnect attempts in the
    // background. Self-initiated disconnects (sign-out, exit-to-menu)
    // do not trigger this callback — see NetworkClient.onDisconnected.
    this.network.onDisconnected((reason) => this.handleServerDisconnect(reason));

    // Premium subscription updates can land at any point in the
    // session lifecycle — login screen, char-select, char-create,
    // pause menu, in-game. Register the handler once on the network
    // client itself (which lives for the entire app lifetime) so we
    // don't risk missing a webhook push because the player happened
    // to be on a screen that hadn't installed its handlers yet.
    this.network.on(MessageType.PremiumUpdate, (msg) => {
      this.applyPremiumPush(msg as PremiumUpdateMsg);
    });

    // Account deletion ack. Installed up-front (like PremiumUpdate) so it
    // fires regardless of which screen the player is on when the server
    // confirms. Registered once for the lifetime of the network client.
    this.network.on(MessageType.AccountDeleted, (msg) => {
      this.handleAccountDeleted(msg as AccountDeletedMsg);
    });
    this.setLoadProgress(80);

    this.controls = new ControlsHelp(hudContainer);
    this.setLoadProgress(85);

    // Server-authoritative onboarding. The overlay requests phase
    // transitions through the callback; the server validates each
    // transition and echoes back via `TutorialPhase`. The `'done'`
    // transition triggers a server-side warp to sector 0,0,0 — we
    // hide the overlay once the server acknowledges the transition.
    this.tutorial = new Tutorial(
      hudContainer,
      (phase) => {
        this.network.sendTutorialAdvance(phase);
      },
      () => {
        this.controls.showHint();
      },
    );
    this.setLoadProgress(88);

    this.audio.setMusicEnabled(this.loadMusicEnabled());

    this.pauseMenu = new PauseMenu(
      hudContainer,
      (action) => this.handlePauseAction(action),
      {
        music: {
          isEnabled: () => this.audio.isMusicEnabled(),
          toggle: () => {
            const next = !this.audio.isMusicEnabled();
            this.audio.setMusicEnabled(next);
            this.saveMusicEnabled(next);
            this.audio.playUI('select');
          },
        },
        // The menu calls this every time it opens, so a webhook-driven
        // premium flip is reflected the next time the player pauses
        // without us having to plumb a setter.
        isPremium: () => this.premium.active,
      },
    );
    this.setLoadProgress(92);

    this.authScreen = new AuthScreen(
      hudContainer,
      (result) => this.handleAuth(result),
      // Surface the resolved server URL on the login screen for on-device
      // debugging. The APK / IPA loads the bundle from localhost so there
      // is no address bar — this footer is our only way to verify the
      // build is pointed at the right endpoint without remote inspector.
      resolveServerUrl(),
      // Forgot-password round-trips run on their own short-lived socket
      // (see NetworkClient) so they can't disturb an in-flight login.
      {
        request: (email) =>
          this.network.requestPasswordReset(resolveServerUrl(), email),
        confirm: (email, code, newPassword) =>
          this.network.confirmPasswordReset(resolveServerUrl(), email, code, newPassword),
      },
    );
    this.setLoadProgress(100);

    this.createDebugPanel(hudContainer);

    // Track raw KeyE state for the surface-ruin hold-to-investigate
    // interaction (Phase 2.1). The flag is read each frame in
    // tickRuinInvestigate. Listener is registered once here and
    // intentionally never removed — the page-lifetime is short and
    // the listener is idle when no ruin is in range.
    window.addEventListener('keydown', (e) => {
      if (e.code === 'KeyE' && !e.repeat) {
        this._keyEHeld = true;
      }
    });
    window.addEventListener('keyup', (e) => {
      if (e.code === 'KeyE') {
        this._keyEHeld = false;
      }
    });
    window.addEventListener('keydown', (e) => {
      // The full comms window swallows all game hotkeys while open;
      // it handles Escape itself via a capture-phase listener.
      if (this.chatWindow?.visible) return;
      if (e.code === 'Escape') {
        e.preventDefault();
        if (this.tutorial?.isActive()) {
          return;
        }
        if (this.friendsPanel.visible) {
          this.friendsPanel.hide();
          this.input.enable();
        } else if (this.leaderboard.isVisible()) {
          this.closeLeaderboard();
        } else if (this.warpGateUI.isVisible()) {
          this.closeWarpGateUI();
        } else if (this.universeMap.isVisible()) {
          this.closeUniverseMap();
        } else if (this.missionJournal.isVisible()) {
          this.closeMissionJournal();
        } else if (this.inventoryPanel.visible) {
          this.inventoryPanel.hide();
          this.input.enable();
        } else if (this.docked) {
          this.undockFromStation();
        } else if (this.running && !this.paused) {
          this.pauseGame();
        }
        return;
      }
      if (e.code === 'F3' && this.running) {
        e.preventDefault();
        this.debugVisible = !this.debugVisible;
        if (this.debugEl) {
          this.debugEl.style.display = this.debugVisible ? 'block' : 'none';
        }
      }
      if (e.code === 'KeyF' && this.running && !this.paused && !this.docked) {
        const stationId = this.hud.nearbyStationId;
        if (stationId) {
          e.preventDefault();
          this.dockAtStation(stationId);
        }
      }
      if (e.code === 'KeyT' && this.running && !this.paused && !this.docked && !this.activeMerchantId) {
        const merchantId = this.hud.nearbyMerchantId;
        if (merchantId) {
          e.preventDefault();
          this.hailMerchant(merchantId);
        }
      }
      if (e.code === 'KeyG' && this.running && !this.paused && !this.docked && !this.warpGateUI.isVisible()) {
        const gateId = this.hud.nearbyWarpGateId;
        if (gateId) {
          e.preventDefault();
          this.openWarpGateUI(gateId);
        }
      }
      if (
        e.code === 'KeyP' &&
        this.running &&
        !this.paused &&
        !this.docked &&
        !this.warpGateUI.isVisible() &&
        !this.universeMap.isVisible() &&
        !this.inventoryPanel.visible &&
        !this.chat.isInputFocused?.()
      ) {
        if (this.onSurface) {
          e.preventDefault();
          this.network.sendLiftoffRequest();
        } else {
          const planetId = this.hud.nearbyPlanetId;
          if (planetId) {
            e.preventDefault();
            this.tryLandOnPlanet(planetId);
          }
        }
      }
      if (e.code === 'KeyI' && this.running && !this.docked) {
        e.preventDefault();
        if (this.inventoryPanel.visible) {
          this.inventoryPanel.hide();
          this.input.enable();
        } else {
          this.inventoryPanel.show();
          this.input.disable();
          this.audio.playUI('select');
        }
      }
      if (
        e.code === 'KeyJ' &&
        this.running &&
        !this.paused &&
        !this.warpGateUI.isVisible() &&
        !this.universeMap.isVisible() &&
        !this.inventoryPanel.visible &&
        !this.docked &&
        !this.chat.isInputFocused?.()
      ) {
        e.preventDefault();
        this.toggleMissionJournal();
      }
      if (
        e.code === 'KeyL' &&
        this.running &&
        !this.paused &&
        !this.warpGateUI.isVisible() &&
        !this.universeMap.isVisible() &&
        !this.inventoryPanel.visible &&
        !this.docked &&
        !this.chat.isInputFocused?.()
      ) {
        e.preventDefault();
        if (this.leaderboard.isVisible()) {
          this.closeLeaderboard();
        } else {
          this.openLeaderboard();
        }
      }
      if (
        e.code === 'KeyM' &&
        this.running &&
        !this.paused &&
        !this.warpGateUI.isVisible() &&
        !this.chat.isInputFocused?.()
      ) {
        e.preventDefault();
        if (this.universeMap.isVisible()) {
          this.closeUniverseMap();
        } else {
          this.openUniverseMap();
        }
      }
      if (
        e.code === 'KeyU' &&
        this.running &&
        !this.paused &&
        !this.docked &&
        !this.warpGateUI.isVisible() &&
        !this.universeMap.isVisible() &&
        !this.inventoryPanel.visible &&
        !this.chat.isInputFocused?.()
      ) {
        e.preventDefault();
        if (this.friendsPanel.visible) {
          this.friendsPanel.hide();
          this.input.enable();
        } else {
          this.friendsPanel.show();
          this.input.disable();
          this.audio.playUI('select');
        }
      }
      if (
        // "Plus" / "+" / "Add to friends" shortcut: when targeting a
        // player ship in space, hit Y to open the friends panel with the
        // target's callsign prefilled. Y is otherwise unused for flight.
        e.code === 'KeyY' &&
        this.running &&
        !this.paused &&
        !this.docked &&
        !this.chat.isInputFocused?.()
      ) {
        const target = this.worldLabels.currentTarget;
        if (
          target &&
          target.entity.type === EntityType.Ship &&
          target.entity.name &&
          target.entity.name.trim().length > 0
        ) {
          e.preventDefault();
          this.friendsPanel.prefillAddName(target.entity.name);
          this.input.disable();
          this.audio.playUI('select');
        }
      }
    });

    // Intentionally do NOT auto-open the pause/Options menu when pointer
    // lock is released (alt-tab, window blur, clicking outside the canvas,
    // browser-driven Esc exit, etc.). The pause menu must only open via an
    // explicit Escape keypress handled above.

    setTimeout(() => {
      const loading = document.getElementById('loading-screen')!;
      loading.classList.add('hidden');
      setTimeout(() => loading.remove(), 500);

      // Try silent auto-login from saved credentials before falling back
      // to the auth screen. `tryAutoLogin` resolves `true` when it has
      // already routed the player to char-select / char-create, in which
      // case we skip showing the auth screen entirely.
      const showAuth = async () => {
        // Snapshot the saved username BEFORE attempting auto-login —
        // a credential rejection inside `tryAutoLogin` clears the
        // stored token, but we still want to pre-fill the username on
        // the manual form so the player only retypes the password.
        const savedUsername = this.loadSavedCredentials()?.username ?? '';
        if (await this.tryAutoLogin()) return;
        // Desktop / Steam shell: when there are no saved password
        // credentials, fall back to silent Steam-identity login before
        // surfacing the manual form. This is what lets a Steam player
        // launch straight into the game without ever creating an account.
        if (await this.trySteamLogin()) return;
        if (savedUsername) this.authScreen.prefillUsername(savedUsername);
        this.authScreen.show();
      };

      if (this.isMobile) {
        // The startup overlay provides the user-gesture anchor we need to
        // request fullscreen + landscape lock. Once the player taps it
        // we flip to the normal auth screen.
        this.mobileOverlay = new MobileStartupOverlay(
          document.body,
          () => {
            this.tryStartIntroMusic();
            void showAuth();
          },
        );
      } else {
        void showAuth();
        this.armIntroMusicOnFirstGesture();
      }
    }, 500);
  }

  /**
   * Browsers gate AudioContext + HTMLAudioElement.play() behind a user
   * gesture, so on desktop we can't kick the intro bed off as soon as
   * the auth screen appears — we wait for the first pointer/key event
   * anywhere on the document and start the track from inside that
   * gesture. Mobile already has a gesture from the startup-overlay tap,
   * so it calls `tryStartIntroMusic` directly instead of going through
   * this listener.
   */
  private armIntroMusicOnFirstGesture(): void {
    const handler = () => {
      window.removeEventListener('pointerdown', handler, true);
      window.removeEventListener('keydown', handler, true);
      this.tryStartIntroMusic();
    };
    window.addEventListener('pointerdown', handler, true);
    window.addEventListener('keydown', handler, true);
  }

  /**
   * Start the looping pre-game intro track, but only if the player hasn't
   * disabled music in the pause menu. The 60% music-channel gain matches
   * the design spec for the title screen and is restored each time we
   * come back to a pre-game screen (sign-out / disconnect / exit) so the
   * track resumes after the in-game bed is swapped out.
   */
  private tryStartIntroMusic(): void {
    if (!this.audio.isMusicEnabled()) return;
    this.audio.playIntroMusic();
  }

  private wireTouchCallbacks(): void {
    if (!this.touchControls) return;
    this.touchControls.setCallbacks({
      onDock: () => {
        if (this.docked) {
          this.undockFromStation();
          return;
        }
        const sid = this.hud?.nearbyStationId;
        if (sid) this.dockAtStation(sid);
      },
      onWarp: () => {
        if (!this.running || this.paused || this.docked) return;
        const gid = this.hud?.nearbyWarpGateId;
        if (gid && !this.warpGateUI.isVisible()) this.openWarpGateUI(gid);
      },
      onTrade: () => {
        if (!this.running || this.paused || this.docked) return;
        const mid = this.hud?.nearbyMerchantId;
        if (mid && !this.activeMerchantId) this.hailMerchant(mid);
      },
      onLand: () => {
        // Mirror the desktop `P` key dispatch (see the KeyP handler
        // above): if we're on a surface, request liftoff; otherwise
        // request landing on the closest planet inside the LAND
        // radius. The `nearbyPlanetId` / `nearSpaceport` proximity
        // gates were already evaluated by the HUD before showLand()
        // exposed the button, so the network message can fail
        // server-side only via legitimate races (warp transition,
        // sector boundary) — same risk surface as the desktop key.
        if (!this.running || this.paused || this.docked) return;
        if (this.onSurface) {
          this.network.sendLiftoffRequest();
          return;
        }
        const planetId = this.hud?.nearbyPlanetId;
        if (planetId) this.tryLandOnPlanet(planetId);
      },
      onWeaponCycle: () => {
        this.input.cycleWeaponSlot(1);
      },
      onInventory: () => {
        if (!this.running || this.docked) return;
        if (this.inventoryPanel.visible) {
          this.inventoryPanel.hide();
          if (!this.paused) this.input.enable();
        } else {
          this.inventoryPanel.show();
          this.input.disable();
          this.audio?.playUI('select');
        }
      },
      onMap: () => {
        if (!this.running || this.paused || this.warpGateUI.isVisible()) return;
        if (this.universeMap.isVisible()) {
          this.closeUniverseMap();
        } else {
          this.openUniverseMap();
        }
      },
      onMenu: () => {
        if (!this.running) return;
        if (this.pauseMenu.isVisible()) {
          this.resumeGame();
        } else {
          this.pauseGame();
        }
      },
      onFriends: () => {
        if (!this.running || this.paused || this.docked) return;
        if (this.friendsPanel.visible) {
          this.friendsPanel.hide();
          this.input.enable();
        } else {
          this.friendsPanel.show();
          this.input.disable();
          this.audio?.playUI('select');
        }
      },
      onMissions: () => {
        if (!this.running || this.paused || this.docked) return;
        this.toggleMissionJournal();
      },
      // CHAT / USE touch callbacks are intentionally omitted — their
      // pills were removed from the top bar. Chat is opened by tapping
      // the chat panel directly (see Chat mobile wiring); consumable
      // usage is handled from the Inventory panel's Consumables tab.
      onToggleAutoFire: (enabled) => {
        // The fire pill is a toggle on mobile; surface the state
        // change as a HUD notification so the label change ("FIRE"
        // ↔ "AUTO") isn't a mystery. 'success' reads as green for
        // enabled and 'warning' as amber for disabled.
        this.hud?.showNotification(
          enabled ? t('touch.autoFireOn') : t('touch.autoFireOff'),
          enabled ? 'success' : 'warning',
        );
        this.audio?.playUI('select');
      },
      onToggleBoost: (enabled) => {
        // Boost pill is now also a toggle. The touch layer only
        // actually reports `isBoosting=true` when the pilot is also
        // pushing thrust, so idle-and-armed is free; we surface the
        // arm/disarm edge as a notification for parity with auto-fire.
        this.hud?.showNotification(
          enabled ? t('touch.boostEnabled') : t('touch.boostDisabled'),
          enabled ? 'success' : 'warning',
        );
        this.audio?.playUI('select');
      },
    });
  }

  private setLoadProgress(pct: number) {
    const fill = document.getElementById('load-progress');
    if (fill) fill.style.width = `${pct}%`;
  }

  /**
   * Mobile-only "magnetic crosshair" aim assist. When a valid combat
   * target (NPC / enemy ship / asteroid) sits near the center of the
   * screen we slide the crosshair toward its center at a fixed pixel-
   * per-second rate, scaled down by distance from the disk and by how
   * hard the pilot is currently pushing the look stick.
   *
   * Why pixel-per-second instead of error × K (the previous version):
   * an angular-error PID converges asymptotically — when you're 5px
   * off it pulls 5x slower than when 50px off, so the "last few pixels
   * onto the target" feels like the assist gives up exactly when you
   * need it. A flat px/sec snap closes the final pixels in a handful
   * of frames, which is what players read as "the aim helped me hit".
   *
   * Why "very light" still applies:
   *   - 18% of screen disk only — aiming *past* a target to line up a
   *     distant one isn't hijacked.
   *   - Quadratic falloff from disk center to edge — the rim pull is
   *     ~1/16th of the center pull, so it's barely a nudge there.
   *   - Stick-activity gain — when the pilot pushes the look stick
   *     hard, assist drops to ~30%. They can always flick away to
   *     break the pull and acquire a different target.
   *   - Per-frame angular cap — even at the very center the crosshair
   *     can move at most ~3°/frame from assist (≈170°/sec), so a
   *     target warping right on top of us can't yank the camera.
   *   - Desktop / keyboard / gamepad untouched: the touch overlay
   *     visibility is the only thing that turns this on.
   *
   * We use `worldLabels.currentTarget` from the previous frame
   * (worldLabels hasn't been updated yet this tick). At 60fps that's
   * ~16ms of staleness, which is well below perceptual threshold and
   * lets the assist run in-band with `network.sendInput` so the
   * server-authoritative ship ends up at the same heading our local
   * prediction does.
   */
  private applyMobileAimAssist(inputState: InputState, dt: number): void {
    if (!this.touchControls || !this.touchControls.isVisible()) return;
    if (this.docked || this.paused) return;
    const target = this.worldLabels?.currentTarget;
    if (!target) return;

    const kind = target.entity.type;
    const assistable =
      kind === EntityType.NPC ||
      kind === EntityType.Ship ||
      kind === EntityType.Asteroid;
    if (!assistable) return;

    const w = window.innerWidth;
    const h = window.innerHeight;
    const dxPx = target.screenX - w * 0.5;
    const dyPx = target.screenY - h * 0.5;
    const pxDist = Math.hypot(dxPx, dyPx);
    const radius = Math.min(w, h) * 0.18;
    if (pxDist < 1 || pxDist >= radius) return;

    const camThree = this.camera.getCamera();
    const fovRad = (camThree.fov * Math.PI) / 180;
    // Radians per CSS pixel. Both axes share the vertical-FOV factor;
    // the horizontal FOV is slightly wider via aspect, but at the
    // small pixel offsets this assist operates in (inside an 18%-of-
    // screen disk around center) the approximation is inside one
    // tenth of a degree — well under our assist rate's noise floor.
    const radPerPx = fovRad / h;

    // Quadratic falloff: 1 at the disk's center → 0 at the rim. Biases
    // the magnetic pull strongly toward "already aiming at the target"
    // so the rim of the disk barely contributes, but the last few
    // pixels onto the center get a real snap.
    const f = 1 - pxDist / radius;
    const falloff = f * f;

    // Stick-activity gain: when the pilot pushes the look stick hard
    // (e.g. flicking past the current target onto a different one),
    // dial the assist down so it doesn't fight them. The stick's
    // contribution to inputState.rotation this frame is bounded by
    // the *effective* (sensitivity-scaled) touch look-rate × dt; we
    // normalize against that to get a 0..1 "how busy is the pilot"
    // score. Using the effective rate (rather than the raw base)
    // keeps the gain consistent across the user's sensitivity slider
    // — otherwise a pilot at 0.5× sensitivity would always read as
    // "barely pushing" and assist would never back off.
    const stickRotMag = Math.hypot(
      inputState.rotation.x,
      inputState.rotation.y,
    );
    const stickBudget = getEffectiveTouchLookRate() * dt;
    const stickActivity = stickBudget > 0
      ? Math.min(1, stickRotMag / stickBudget)
      : 0;
    const stickGain = 1 - 0.7 * stickActivity;

    // Magnetic snap velocity: pixels-per-second the crosshair moves
    // toward the target at the disk's center, before falloff and stick
    // gain. 70 px/sec at 60fps = ~1.17 px/frame — closes a 10px gap in
    // ~140ms (a hair faster than a typical thumb correction), which
    // is enough for "the aim helped" without being instant.
    const PX_PER_SEC = 70;
    const snapPx = PX_PER_SEC * falloff * stickGain * dt;
    // Move-toward-target as a unit vector × snapPx, capped so we never
    // overshoot the target this frame (avoids visible jitter at
    // sub-pixel error).
    const movePx = Math.min(snapPx, pxDist);
    const dirYawPx = -dxPx * (movePx / pxDist);
    const dirPitchPx = -dyPx * (movePx / pxDist);
    const yawBias = dirYawPx * radPerPx;
    const pitchBias = dirPitchPx * radPerPx;

    // Per-frame angular cap as a final safety: even at full effective
    // pull, the assist can't move the crosshair more than ~3°/frame
    // (≈170°/sec at 60fps). Prevents a target warping right on top
    // of the player from yanking the camera.
    const MAX_PER_FRAME = 0.05;
    const biasY = Math.max(-MAX_PER_FRAME, Math.min(MAX_PER_FRAME, yawBias));
    const biasX = Math.max(-MAX_PER_FRAME, Math.min(MAX_PER_FRAME, pitchBias));

    inputState.rotation.y += biasY;
    inputState.rotation.x += biasX;
  }

  private handleAuth(result: AuthResult): void {
    const serverUrl = resolveServerUrl();

    // Forward the (possibly null) attribution snapshot. Server-side
    // signup persists it on the new account row; login / guest modes
    // ignore it. Pulling it from storage rather than re-reading the
    // URL means a player who refreshed the tab or lost the query
    // string mid-flow still gets credit attached to their first-touch
    // campaign.
    const utm = result.mode === 'signup'
      ? (getStoredAttribution() ?? undefined)
      : undefined;

    this.network.authenticate(
      serverUrl,
      result.mode,
      result.username,
      result.password,
      result.email,
      utm,
    ).then((resp) => {
      if (!resp.success) {
        // Server actively rejected the credentials (wrong password, name
        // taken on signup, …). A persisted token can no longer be
        // assumed valid, so wipe it before bouncing the user back to the
        // form to retype.
        this.clearSavedCredentials();
        this.authScreen.showError(resp.error ?? 'Authentication failed');
        this.authScreen.resetForm();
        return;
      }

      // Only stash credentials for real accounts. Guest mode mints a
      // random username server-side and is meant to be ephemeral.
      if (
        (result.mode === 'login' || result.mode === 'signup') &&
        result.password
      ) {
        this.saveCredentials(resp.username, result.password);
      }

      // Successful signup: drop the cached attribution so a second
      // account created from the same browser/device doesn't inherit
      // a stale campaign tag. Logins leave it alone — they didn't
      // consume it server-side anyway.
      if (result.mode === 'signup') {
        clearStoredAttribution();
      }

      this.applyAuthSuccess(resp, result);
    }).catch((err: unknown) => {
      const msg = err instanceof Error && err.message ? err.message : 'Connection failed';
      // Also log the full details to the console so Safari / Chrome remote
      // inspector (or adb logcat) shows the close code and URL for the
      // packaged native builds where we have no in-page dev tools.
      // eslint-disable-next-line no-console
      console.error('[auth] connect failed', { serverUrl, err });
      // Connection failure (offline, bad URL, TLS) ≠ bad credentials, so
      // we keep the saved token — next launch (with the server back) can
      // still auto-login.
      this.authScreen.showError(msg);
      this.authScreen.resetForm();
    });
  }

  /**
   * Wire up the post-authentication state and route to the correct
   * downstream screen (character roster or character-create). Shared
   * between the manual {@link handleAuth} flow and {@link tryAutoLogin}
   * so both code paths produce identical session state.
   */
  private applyAuthSuccess(
    resp: {
      username: string;
      playerId?: string;
      characters: SavedCharacterMsg[];
      premium?: { active: boolean; currentPeriodEnd: number | null };
      role?: 'admin' | 'player';
    },
    result: { mode: AuthResult['mode']; password?: string; email?: string },
  ): void {
    this.currentUser = {
      userId: resp.username,
      username: resp.username,
      token: '',
      isNewPlayer: result.mode === 'signup',
      mode: result.mode,
      password: result.password,
      email: result.email,
    };
    this.premium = resp.premium ?? { active: false, currentPeriodEnd: null };
    this.hud?.setPremium?.(this.premium.active);
    this.isAdmin = resp.role === 'admin';
    this.billingToken = resp.playerId ?? null;
    this.authScreen.hide();
    this.trackAuthEvent(result.mode);

    // Alias the (until now anonymous) RevenueCat customer to the game
    // account so the Pro entitlement follows the player across devices
    // and app stores. The username is the stable account id — the
    // playerId above is session-scoped and would fork a new RevenueCat
    // customer every login. Fire-and-forget: a billing hiccup must
    // never delay the character roster. No-op outside Capacitor.
    void identifyRevenueCatUser(resp.username).then((info) => {
      if (info) this.applyNativeProStatus(proStatusFrom(info));
    });

    this.serverCharacters = (resp.characters ?? []).map((c) => ({
      id: c.id,
      callsign: c.callsign,
      race: c.race,
      shipClass: c.shipClass,
      level: c.level,
      rank: c.rank,
      credits: c.credits,
      lastPlayed: c.lastPlayed,
      // Preserve the hangar list so a later `handleCharSelected` →
      // `saveCharacter` round-trip echoes the full ownership set back to
      // the server instead of dropping it (which silently wiped every
      // hull the pilot had purchased beyond the active one).
      ownedShipClasses: c.ownedShipClasses ? c.ownedShipClasses.slice() : undefined,
    }));

    if (this.serverCharacters.length === 0) {
      this.showCharCreate();
    } else {
      this.showCharSelect(this.serverCharacters);
    }
  }

  /**
   * Fire the GA4 sign_up / login conversion event so paid-acquisition
   * campaigns can attribute installs back to the registration step.
   * The Reddit Pixel SignUp conversion fires alongside it for Reddit
   * ad-campaign attribution. Both tags load async from index.html;
   * calls before the scripts arrive buffer through `window.dataLayer` /
   * `rdt.callQueue` so this is safe to call at any point. Wrapped in
   * guards + try/catch so a missing or broken analytics setup never
   * breaks the login flow.
   */
  private trackAuthEvent(mode: AuthResult['mode']): void {
    if (typeof window === 'undefined') return;
    const gtag = window.gtag;
    if (typeof gtag === 'function') {
      try {
        if (mode === 'signup') {
          gtag('event', 'sign_up', { method: 'email' });
        } else if (mode === 'login') {
          gtag('event', 'login', { method: 'email' });
        } else if (mode === 'steam') {
          gtag('event', 'login', { method: 'steam' });
        }
      } catch {
        // Analytics is best-effort; never let a tracking failure surface
        // to the player or block the post-auth flow.
      }
    }
    const rdt = window.rdt;
    if (typeof rdt === 'function' && mode === 'signup') {
      try {
        rdt('track', 'SignUp');
      } catch {
        // Same best-effort contract as gtag above.
      }
    }
  }

  /**
   * Spin up the admin-only perf monitor. Called after chat is ready
   * (so we can route alerts through `chat.addActivity`) and only when
   * the most recent auth response set `isAdmin = true`. Idempotent so
   * a re-login during the same session doesn't double-start the
   * polling timer.
   */
  private startPerfMonitorIfAdmin(): void {
    if (!this.isAdmin) return;
    if (!this.perfMonitor) {
      this.perfMonitor = new PerfMonitor({
        onAlert: (text, color) => this.chat.addActivity(text, color),
      });
    }
    this.perfMonitor.start();
  }

  /**
   * Intercept slash commands the client wants to react to *locally*
   * before/instead of forwarding to the server. Currently handles the
   * admin-only `/perf` and `/mem` aliases — we print the browser-side
   * snapshot immediately so an admin gets the client view in chat
   * even when the round-trip to the server is slow / dropped, and
   * still forward the command so the server's GM handler appends its
   * own snapshot. Returns `true` when the command was recognised so
   * callers can choose to short-circuit rendering if they want; the
   * current call site forwards either way.
   */
  private handleClientSlashCommand(raw: string): boolean {
    if (!raw.startsWith('/')) return false;
    const trimmed = raw.slice(1).trim().toLowerCase();
    if (trimmed === 'perf' || trimmed === 'mem') {
      if (!this.isAdmin) return false;
      const sample = this.perfMonitor?.snapshot();
      if (sample) {
        const line = formatPerfLine(sample);
        this.chat.addActivity(line, '#88ccff');
        // eslint-disable-next-line no-console
        console.info('[perf] /perf snapshot', sample);
      }
      return true;
    }
    return false;
  }

  /**
   * Best-effort auto-login at startup. Returns `true` when the saved
   * credentials authenticated successfully and we've routed into the
   * character flow — caller can then skip showing the auth screen.
   *
   * Failures fall into two buckets:
   *   - Credential rejection (`success: false`): stored token is wrong
   *     (server password reset, account deleted), so we clear it.
   *   - Connection failure (network down, server offline): we keep the
   *     token so the next launch with a reachable server still works.
   * In both cases we return `false` so the caller surfaces the auth
   * screen.
   */
  private async tryAutoLogin(): Promise<boolean> {
    const saved = this.loadSavedCredentials();
    if (!saved) return false;

    const serverUrl = resolveServerUrl();
    try {
      const resp = await this.network.authenticate(
        serverUrl,
        'login',
        saved.username,
        saved.password,
      );
      if (!resp.success) {
        // eslint-disable-next-line no-console
        console.warn('[auth] auto-login rejected:', resp.error);
        this.clearSavedCredentials();
        return false;
      }
      this.applyAuthSuccess(resp, {
        mode: 'login',
        password: saved.password,
      });
      return true;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[auth] auto-login failed (connect error):', err);
      return false;
    }
  }

  /**
   * Desktop / Steam-only silent login from the player's Steam identity.
   * Resolves `true` when the player has been authenticated and routed to
   * char-select / char-create (so the caller skips the auth screen).
   *
   * Returns `false` — never throws — when this isn't the Steam shell,
   * Steam isn't running, or the server couldn't verify the ticket, in
   * which case the caller falls through to the normal auth screen so the
   * player can still log in with a username/password.
   */
  private async trySteamLogin(): Promise<boolean> {
    if (!isElectronDesktop()) return false;
    const steam = window.zerionDesktop?.steam;
    if (!steam) return false;
    try {
      if (!(await steam.isAvailable())) return false;
    } catch {
      return false;
    }

    const serverUrl = resolveServerUrl();
    try {
      const resp = await this.network.authenticate(serverUrl, 'steam', '');
      if (!resp.success) {
        // eslint-disable-next-line no-console
        console.warn('[auth] steam login rejected:', resp.error);
        return false;
      }
      this.applyAuthSuccess(resp, { mode: 'steam' });
      return true;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[auth] steam login failed (connect error):', err);
      return false;
    }
  }

  private showCharCreate(): void {
    const hudContainer = document.getElementById('hud')!;
    this.charCreate?.dispose();
    this.charCreate = new CharCreateScreen(
      hudContainer,
      (data) => this.handleCharCreated(data),
      () => this.handleBackFromCreatePilot(),
      { hasPremium: this.premium.active },
    );
    this.charCreate.show();
  }

  /** Leave create-pilot: return to the roster if one exists, else to login. */
  private handleBackFromCreatePilot(): void {
    this.charCreate?.hide();
    this.charCreate?.dispose();
    this.charCreate = null;
    if (this.serverCharacters.length > 0) {
      this.showCharSelect(this.serverCharacters);
    } else {
      this.handleSignOut();
    }
  }

  private showCharSelect(characters: SavedCharacter[]): void {
    const hudContainer = document.getElementById('hud')!;
    // Re-entries (e.g. after creating a new pilot) tear down the previous
    // instance so we don't leak its per-card WebGL contexts.
    this.charSelect?.dispose();
    this.charSelect = new CharSelectScreen(
      hudContainer,
      this.currentUser!.username,
      characters,
      (character) => this.handleCharSelected(character),
      () => {
        this.charSelect?.dispose();
        this.charSelect = null;
        this.showCharCreate();
      },
      () => this.handleSignOut(),
      {
        isPremium: () => this.premium.active,
        onUpgrade: () => this.openUpgradeModal('pilot-select'),
      },
    );
    this.charSelect.show();
  }

  private handleCharCreated(data: CharacterData): void {
    const character: SavedCharacter = {
      id: crypto.randomUUID(),
      callsign: data.callsign,
      race: data.race,
      shipClass: data.shipClass,
      level: 1,
      rank: 0,
      credits: 500,
      lastPlayed: Date.now(),
      // Starter hull is immediately in the hangar. Additional hulls are
      // added via the station's Hangar tab → Buy flow.
      ownedShipClasses: [data.shipClass],
    };

    this.network.saveCharacter(character);
    this.serverCharacters.push(character);

    this.charCreate?.hide();
    this.charCreate?.dispose();
    this.charCreate = null;
    this.startGame(character, true);
  }

  private handleCharSelected(character: SavedCharacter): void {
    character.lastPlayed = Date.now();
    this.network.saveCharacter(character);

    // Dispose the char-select screen so its per-card WebGL contexts
    // are released before the in-game renderer takes over the GPU.
    this.charSelect?.dispose();
    this.charSelect = null;
    this.startGame(character, false);
  }

  private handleSignOut(): void {
    // Sign-out is the explicit "I'm done with this account on this
    // device" gesture, so wipe the saved credentials. Without this, the
    // next launch would silently auto-login back into the account the
    // user just signed out of.
    this.clearSavedCredentials();
    // Detach the RevenueCat customer so the next account signing in on
    // this device starts anonymous instead of inheriting this player's
    // store entitlements. No-op outside the Capacitor shells.
    void resetRevenueCatUser();
    this.currentUser = null;
    this.serverCharacters = [];
    this.isAdmin = false;
    this.perfMonitor?.stop();
    this.charCreate?.hide();
    this.charCreate?.dispose();
    this.charSelect?.dispose();
    this.charCreate = null;
    this.charSelect = null;
    this.network.disconnect();
    this.authScreen.show();
    this.tryStartIntroMusic();
  }

  private togglePause(): void {
    if (this.pauseMenu.isVisible()) {
      this.resumeGame();
    } else {
      this.pauseGame();
    }
  }

  /**
   * Handle an unsolicited pointer-lock exit. The browser's pointer-lock
   * release on ESC swallows the corresponding keydown, so we can't rely
   * on the window keydown listener to open the pause menu on the first
   * press — the InputManager calls us directly instead. We only act
   * during active gameplay so we don't fight UI-driven exits (the
   * tutorial, inventory, station UI, etc. all call `input.disable()`
   * first, which sets `_enabled=false` *before* releasing the lock,
   * which means this callback won't fire for those paths). The chained
   * tutorial / paused / docked checks mirror the window-level ESC
   * handler so the two paths agree on what "ESC during gameplay"
   * means.
   */
  private handlePointerLockEscape(): void {
    if (this.tutorial?.isActive()) return;
    if (!this.running || this.paused) return;
    if (this.docked) return;
    // Any open UI panel (inventory / friends / leaderboard / mission
    // journal / universe map / warp gate / pause menu) already disabled
    // input before exiting the lock, so `_enabled` would have been
    // false in the InputManager and this callback would not have run.
    // The check is therefore implicit.
    this.pauseGame();
  }

  private pauseGame(): void {
    if (this.paused) return;
    this.paused = true;
    this.input.disable();
    this.pauseMenu.show();
    this.audio.playUI('select');
  }

  private resumeGame(): void {
    if (!this.paused) return;
    this.paused = false;
    this.pauseMenu.hide();
    this.input.enable();
    // Hand mouse-look straight back when the menu closes instead of
    // forcing the player to click the canvas to re-lock the pointer.
    // This runs inside the originating gesture (resume button / backdrop
    // click), which the browser requires for requestPointerLock. When
    // resumed via the Esc key the browser may refuse during its post-Esc
    // cooldown, in which case the player clicks once to lock — same as
    // before. No-op on touch devices (the lock API is feature-detected).
    this.input.acquirePointerLock();
  }

  private handlePauseAction(action: PauseAction): void {
    switch (action) {
      case 'resume':
        this.resumeGame();
        break;
      case 'controls':
        this.resumeGame();
        this.controls.show();
        break;
      case 'leaderboard':
        this.resumeGame();
        this.openLeaderboard();
        break;
      case 'friends':
        this.resumeGame();
        if (!this.docked) {
          this.friendsPanel.show();
          this.input.disable();
          this.audio?.playUI('select');
        }
        break;
      case 'missions':
        this.resumeGame();
        if (!this.docked) {
          this.openMissionJournal();
        }
        break;
      case 'charSelect':
        this.exitToCharSelect();
        break;
      case 'signOut':
        this.exitAndSignOut();
        break;
      case 'deleteAccount':
        this.requestAccountDeletion();
        break;
      case 'upgrade':
        this.openUpgradeModal('pause-menu');
        break;
    }
  }

  /**
   * Apply a server-pushed `PremiumUpdate` (Stripe webhook landed) to
   * every client surface that cares: the in-memory `premium` snapshot,
   * the upgrade modal (if open), the char-select roster banner, the
   * char-create locked-card grid, and a HUD toast so the player knows
   * the upgrade went through. Called from a single network handler so
   * a webhook firing while the player is on the leaderboard / friends
   * panel / station UI still updates the underlying state.
   */
  private applyPremiumPush(msg: PremiumUpdateMsg): void {
    const wasActive = this.premium.active;
    this.premium = {
      active: !!msg.active,
      currentPeriodEnd: msg.currentPeriodEnd ?? null,
    };
    this.upgradeModal?.notifyPremium(this.premium);
    this.charSelect?.refreshUpgradeBanner?.();
    this.charCreate?.setPremium?.(this.premium.active);
    this.warpGateUI?.setPremium?.(this.premium.active);
    this.hud?.setPremium?.(this.premium.active);
    if (msg.active && !wasActive) {
      this.hud?.showNotification?.(t('upgrade.activeBadge'), 0xffb000);
      this.audio?.playUI?.('confirm');
    } else if (!msg.active && wasActive) {
      this.hud?.showNotification?.(t('char.premiumLockBanner'), 0x888888);
    }
  }

  /**
   * Merge a RevenueCat Pro-entitlement snapshot into the client's
   * premium state. Fired by the SDK's customer-info listener right
   * after a paywall purchase / restore / renewal, and by the post-auth
   * identity sync. The server (Stripe / Steam) stays authoritative for
   * gameplay perks — the store entitlement only ever *adds* premium on
   * this device, and we never clear an active server-granted premium
   * just because the store entitlement is missing (a Stripe subscriber
   * playing on their phone has no App Store subscription at all).
   * Server-side awareness of store purchases lands with the RevenueCat
   * webhook integration.
   */
  private applyNativeProStatus(status: ProStatus): void {
    if (!status.active || this.premium.active) return;
    this.applyPremiumPush({
      type: MessageType.PremiumUpdate,
      active: true,
      currentPeriodEnd: status.expirationMillis,
    });
  }

  /**
   * Show the premium upgrade modal. Called from both the pilot
   * selection screen ("UNLOCK PREMIUM" CTA) and the in-game pause menu.
   * Lazily constructs the modal on first use; subsequent opens reuse
   * the same instance so the WebSocket-driven `PremiumUpdate` push has
   * a stable target to update via `notifyPremium`. After it closes we
   * always re-render the calling surface (char-select roster / pause
   * menu items / char-create cards) so a successful upgrade is
   * reflected immediately even if the player closed the modal before
   * the webhook landed.
   */
  private openUpgradeModal(
    source: 'pilot-select' | 'pause-menu' | 'char-create' | 'shop' | 'warp-gate' | 'planet',
  ): void {
    const hudContainer = document.getElementById('hud');
    if (!hudContainer) return;
    if (!this.upgradeModal) {
      this.upgradeModal = new UpgradeModal(hudContainer, {
        serverUrl: resolveServerUrl(),
        billingToken: this.billingToken,
        initialPremium: this.premium,
        source,
      });
    } else {
      this.upgradeModal.setBillingToken(this.billingToken);
      this.upgradeModal.notifyPremium(this.premium);
    }
    this.upgradeModal.show(() => {
      this.charSelect?.refreshUpgradeBanner?.();
      this.charCreate?.setPremium?.(this.premium.active);
    });
    this.audio?.playUI('select');
  }

  private exitToCharSelect(): void {
    this.stopGame();
    this.showCharSelect(this.serverCharacters);
    this.tryStartIntroMusic();
  }

  private exitAndSignOut(): void {
    this.stopGame();
    this.handleSignOut();
  }

  /**
   * Fire the account-deletion request. The pause menu has already shown
   * the irreversible-action confirmation dialog, so we just ask the
   * server to delete; the actual local teardown happens in
   * {@link handleAccountDeleted} once the server acks.
   */
  private requestAccountDeletion(): void {
    this.network.sendDeleteAccount();
  }

  /**
   * Server acknowledged (or rejected) an account-deletion request.
   *
   * On success the account + every pilot record is already gone
   * server-side and the socket is being closed, so we tear the local
   * session down exactly like a sign-out and surface a confirmation
   * banner on the auth screen. On failure we leave the player where
   * they are and toast the error so they can retry.
   */
  private handleAccountDeleted(msg: AccountDeletedMsg): void {
    if (!msg.success) {
      this.hud?.showNotification(t('account.delete.failed'), 0xff5555);
      return;
    }
    // Mirror the sign-out teardown: stop the game (if running), wipe
    // saved credentials, drop the socket, and return to the auth screen.
    // Clearing `currentUser` inside handleSignOut also makes the
    // imminent socket-close a no-op in handleServerDisconnect.
    if (this.running) this.stopGame();
    this.handleSignOut();
    this.authScreen.showError(t('account.deleted.banner'));
  }

  /**
   * Involuntary server disconnect (server crash, network drop, kick).
   * Bounces the player back to the auth screen with an error banner
   * instead of letting the network client silently retry-loop while the
   * in-game UI sits stale on top of a dead socket.
   *
   * Self-initiated disconnects (sign-out, exit-to-menu) do NOT route
   * here — `NetworkClient.cleanup()` nulls the socket's `onclose` before
   * closing, so the registered callback only fires for involuntary
   * drops.
   */
  private handleServerDisconnect(reason: string): void {
    // Pre-auth (no user yet): the initial connect attempt's promise
    // rejection is already routed through `showConnectionError` /
    // `authScreen.showError`, so there's nothing to tear down here.
    if (!this.currentUser) return;

    // eslint-disable-next-line no-console
    console.warn('[network] server disconnected:', reason);

    // Tear down the active session. `stopGame()` calls
    // `network.disconnect()` which flips `shouldReconnect = false` in
    // time to suppress the auto-reconnect scheduler that runs further
    // down the same `onclose` handler that fired this callback.
    if (this.running) {
      this.stopGame();
    } else {
      this.network.disconnect();
    }

    this.connectErrorEl?.remove();
    this.connectErrorEl = null;
    this.charCreate?.hide();
    this.charCreate?.dispose();
    this.charCreate = null;
    this.charSelect?.dispose();
    this.charSelect = null;
    this.currentUser = null;
    this.currentCharacter = null;
    this.serverCharacters = [];

    this.authScreen.show();
    const disconnectReason = this.pendingDisconnectReason;
    this.pendingDisconnectReason = null;
    const banner =
      disconnectReason === 'kicked'
        ? t('connError.kicked')
        : t('connError.disconnected');
    this.authScreen.showError(banner);
    this.tryStartIntroMusic();
  }

  private stopGame(): void {
    this.running = false;
    this.paused = false;
    this.pauseMenu.hide();
    this.input.disable();
    this.hud.hide();
    this.chat.hide();
    this.chatWindow.close();
    this.controls.hide();
    this.touchControls?.hide();
    this.damageEffectUnsub?.();
    this.damageEffectUnsub = null;
    this.lastHitConfirmVfxAt.clear();
    this.resetDamageAggregator();
    this.perfMonitor?.stop();
    this.network.disconnect();
    this.currentCharacter = null;
  }

  private jitterVfxPos(center: THREE.Vector3, radius: number): THREE.Vector3 {
    const s = (m: number) => (Math.random() * 2 - 1) * m;
    return new THREE.Vector3(
      center.x + s(radius),
      center.y + s(radius),
      center.z + s(radius),
    );
  }

  /**
   * One VFX per projectile `HitConfirm` (never throttle — a 40ms gate was
   * collapsing entire Gatling volleys to a single flash).
   *
   * The server-supplied `pos` is the projectile collision point at the time
   * of impact, but by the time the message reaches the client (~50–150ms
   * later) the target ship has typically moved several units and the
   * explosion would render *behind* it, looking invisible. We snap the
   * anchor onto the target's current render position when available so
   * the effect always appears on the ship that just got hit.
   */
  private playHitConfirmVfx(targetId: string, pos: THREE.Vector3, isKill: boolean): void {
    if (
      !Number.isFinite(pos.x) || !Number.isFinite(pos.y) || !Number.isFinite(pos.z)
    ) {
      return;
    }
    const anchor = this.resolveVfxAnchor(targetId, pos);
    this.lastHitConfirmVfxAt.set(targetId, performance.now());
    // Hits landing on the LOCAL player ship get a downscaled VFX so
    // the NPC firing at us isn't smothered behind a wall of fireworks
    // — see `spawnMiniExplosion`'s `compact` opt for the full
    // rationale. Kill VFX always fires at full size: you only ever
    // see your own death once, and the dramatic destruction burst is
    // intentional.
    const onLocalPlayer = targetId === this.state.getLocalPlayerId();
    if (isKill) {
      this.effects.spawnDestruction(this.jitterVfxPos(anchor, 0.4), 0xff8844, 60);
    } else {
      this.effects.spawnMiniExplosion(
        this.jitterVfxPos(anchor, onLocalPlayer ? 0.3 : 0.5),
        0xffaa22,
        { compact: onLocalPlayer },
      );
    }
  }

  /**
   * Pick the best world-space anchor for an impact effect: prefer the
   * target's live render position (entity-renderer object), then the
   * latest server transform from local state, finally fall back to the
   * stale projectile-hit position carried in the message.
   */
  private resolveVfxAnchor(
    targetId: string,
    fallback: THREE.Vector3,
  ): THREE.Vector3 {
    const obj = this.entityRenderer.getEntityObject3D(targetId);
    if (obj) {
      return obj.position.clone();
    }
    const ent = this.state.getEntity(targetId);
    if (ent) {
      return new THREE.Vector3(
        ent.transform.position.x,
        ent.transform.position.y,
        ent.transform.position.z,
      );
    }
    return fallback.clone();
  }

  /**
   * When many hits merge into one `EntityUpdate` (no per-hit `HitConfirm`),
   * `damage` is the total hull lost: spawn a short salvo so it still feels
   * like sustained fire. Skipped if `HitConfirm` is currently streaming for
   * the same target (avoids double pops).
   */
  private playMergedHullDamageVfx(
    targetId: string,
    center: THREE.Vector3,
    damage: number,
    isKill: boolean,
  ): void {
    if (
      !Number.isFinite(center.x) || !Number.isFinite(center.y) || !Number.isFinite(center.z)
    ) {
      return;
    }
    const anchor = this.resolveVfxAnchor(targetId, center);
    const now = performance.now();
    const lastHit = this.lastHitConfirmVfxAt.get(targetId) ?? 0;
    if (isKill) {
      if (now - lastHit < 120) return;
      this.effects.spawnDestruction(this.jitterVfxPos(anchor, 0.5), 0xff8844, 60);
      return;
    }
    if (now - lastHit < 80) {
      return;
    }
    // Cap the burst-pop count and shrink each pop's footprint when the
    // damage is landing on the LOCAL player ship. A merged hit can
    // otherwise spawn up to 20 mini-explosions on top of our own hull,
    // and at the chase-camera distance that completely occludes the
    // NPC the player is trying to aim at. The compact variant (~half
    // size) plus a tighter pop cap keeps the impact readable without
    // burying the screen in fire. Other targets (NPCs we're shooting
    // at) keep the original punchy feedback so kills still feel
    // satisfying.
    const onLocalPlayer = targetId === this.state.getLocalPlayerId();
    const popCap = onLocalPlayer ? 6 : 20;
    const popDivisor = onLocalPlayer ? 12 : 6;
    const jitterScale = onLocalPlayer ? 0.6 : 1;
    const pops = Math.min(popCap, Math.max(1, Math.round(damage / popDivisor)));
    for (let i = 0; i < pops; i++) {
      const r = (1.2 + (i % 3) * 0.4) * jitterScale;
      this.effects.spawnMiniExplosion(
        this.jitterVfxPos(anchor, r),
        0xffaa22,
        { compact: onLocalPlayer },
      );
    }
  }

  /**
   * Roll an incoming hit into the per-attacker damage aggregator. The
   * activity line will be flushed to chat after a short idle window
   * ({@link DAMAGE_AGG_IDLE_MS}) — or sooner if the attacker keeps
   * firing for longer than {@link DAMAGE_AGG_MAX_MS}.
   */
  private accumulateDamageTaken(dmg: DamageEventMsg): void {
    const attackerId = dmg.attackerId || 'unknown';
    const now = performance.now();
    const existing = this.damageReceivedAgg.get(attackerId);
    if (existing) {
      clearTimeout(existing.idleTimer);
      existing.hullTotal += dmg.hullDamage;
      existing.shieldTotal += dmg.shieldDamage;
      existing.hits += 1;
      existing.idleTimer = setTimeout(
        () => this.flushDamageAgg(attackerId),
        DAMAGE_AGG_IDLE_MS,
      );
      return;
    }
    const label = this.resolveAttackerLabel(attackerId);
    const idleTimer = setTimeout(
      () => this.flushDamageAgg(attackerId),
      DAMAGE_AGG_IDLE_MS,
    );
    const maxTimer = setTimeout(
      () => this.flushDamageAgg(attackerId),
      DAMAGE_AGG_MAX_MS,
    );
    this.damageReceivedAgg.set(attackerId, {
      attackerId,
      attackerLabel: label,
      hullTotal: dmg.hullDamage,
      shieldTotal: dmg.shieldDamage,
      hits: 1,
      firstAt: now,
      idleTimer,
      maxTimer,
    });
  }

  private flushDamageAgg(attackerId: string): void {
    const agg = this.damageReceivedAgg.get(attackerId);
    if (!agg) return;
    clearTimeout(agg.idleTimer);
    clearTimeout(agg.maxTimer);
    this.damageReceivedAgg.delete(attackerId);

    const total = Math.round(agg.hullTotal + agg.shieldTotal);
    if (total <= 0) return;

    const breakdown = (agg.shieldTotal > 0 && agg.hullTotal > 0)
      ? ' · ' + t('combat.damageBreakdown', {
          shield: String(Math.round(agg.shieldTotal)),
          hull: String(Math.round(agg.hullTotal)),
        })
      : '';

    const knownAttacker = agg.attackerId !== 'unknown' && !!agg.attackerLabel;
    let line: string;
    if (!knownAttacker) {
      line = t('combat.damageTakenUnknown', { dmg: String(total) });
    } else if (agg.hits > 1) {
      line = t('combat.damageTakenHits', {
        attacker: agg.attackerLabel,
        dmg: String(total),
        hits: String(agg.hits),
      });
    } else {
      line = t('combat.damageTaken', {
        attacker: agg.attackerLabel,
        dmg: String(total),
      });
    }
    // Bright red for hull bleed, amber for shield-only pings — same
    // semantic split the floating combat numbers use.
    const color = agg.hullTotal > 0 ? '#ff5555' : '#ffaa44';
    this.chat.addActivity(line + breakdown, color);
  }

  /**
   * Resolve a player-friendly label for the entity that just hit us.
   * For player ships we use the pilot's callsign + level; for NPCs we
   * compose `<Race> <Class> Lv.N`. Falls back to a short id slice so
   * environmental damage (sun corona, despawned shooters) still
   * produces a non-empty line.
   */
  private resolveAttackerLabel(attackerId: string): string {
    if (!attackerId || attackerId === 'unknown') return '';
    const e = this.state.getEntity(attackerId);
    if (!e) return attackerId.slice(0, 6);
    const extra = e.extra ?? {};
    if (e.type === EntityType.Ship && e.name) {
      const lvl = extra['playerLevel'] as number | undefined;
      return lvl != null ? `${e.name} LV.${lvl}` : e.name;
    }
    if (e.type === EntityType.NPC) {
      const lvl = extra['npcLevel'] as number | undefined;
      // Defensive: surface NPCs send shipClass as a NUMBER through the
      // compact wire format. The old `as string` assertion lied at
      // runtime and `.toUpperCase()` threw — same per-frame TypeError
      // that was breaking world-labels (see `normalizeShipClassLabel`
      // in `ui/world-labels.ts` for the long version).
      const shipRaw = extra['shipClass'];
      let ship = 'NPC';
      if (typeof shipRaw === 'string') ship = shipRaw.toUpperCase();
      else if (typeof shipRaw === 'number') {
        const slug = (SHIP_CLASS_KEY as Record<number, string>)[shipRaw];
        ship = (slug ?? 'NPC').toUpperCase();
      }
      const rId = extra['raceId'] as number | undefined;
      const raceRaw = rId !== undefined
        ? (RACE_INFO as Record<number, { name?: string }>)[rId]?.name
        : undefined;
      const raceName = typeof raceRaw === 'string' ? raceRaw.toUpperCase() : '';
      const head = [raceName, ship].filter(Boolean).join(' ');
      return lvl != null ? `${head} LV.${lvl}` : (head || 'NPC');
    }
    return e.name ?? attackerId.slice(0, 6);
  }

  /**
   * Drop any pending damage-received aggregations. Called on respawn /
   * exit-to-menu / sign-out so timers don't fire after we've left
   * the world.
   */
  private resetDamageAggregator(): void {
    for (const agg of this.damageReceivedAgg.values()) {
      clearTimeout(agg.idleTimer);
      clearTimeout(agg.maxTimer);
    }
    this.damageReceivedAgg.clear();
  }

  private async startGame(character: SavedCharacter, isFirstTime: boolean) {
    this.currentCharacter = character;
    setLocale(getLocale() as Locale);

    this.audio.init();
    this.audio.startAmbient();
    // Looping lofi background bed at 10% — quiet enough to sit under the
    // ambient drone + engine noise + UI bleeps without masking them.
    this.audio.startMusic(`${import.meta.env.BASE_URL}music/plasma-lunchbox-lofi.mp3`, 0.1);

    try {
      if (this.network.isConnected()) {
        this.network.joinGame(
          character.callsign,
          character.race,
          getLocale(),
          character.shipClass,
        );
      } else {
        const serverUrl = resolveServerUrl();
        await this.network.connect(
          serverUrl,
          character.callsign,
          character.race,
          getLocale(),
          character.shipClass,
        );
      }
    } catch {
      this.audio.dispose();
      this.showConnectionError(character, isFirstTime);
      return;
    }

    this.damageEffectUnsub?.();
    this.lastHitConfirmVfxAt.clear();

    // Projectiles are broadcast inside normal EntityUpdate batches rather
    // than as explicit EntitySpawn messages, so we hook the game state's
    // spawn event directly.
    this.state.onEntitySpawn((entity) => {
      if (entity.type !== EntityType.Projectile) return;
      const localId = this.state.getLocalPlayerId();
      if (!localId || entity.ownerId !== localId) return;
      // Remember every projectile we shot that did make it onto the wire,
      // so the HitConfirm path can tell point-blank shots (where the
      // projectile entity was destroyed before broadcast) apart from
      // normal-range shots that already played their weapon sound here.
      this.seenProjectileIds.add(entity.id);
      if (this.seenProjectileIds.size > 256) {
        const it = this.seenProjectileIds.values();
        for (let i = 0; i < 64; i++) {
          const next = it.next();
          if (next.done) break;
          this.seenProjectileIds.delete(next.value);
        }
      }
      // Multi-projectile weapons (shotgun/cluster/swarm) emit several spawns
      // in the same tick; collapse them to a single blast sound.
      const nowMs = performance.now();
      if (nowMs - this.lastWeaponSoundAt < 45) return;
      this.lastWeaponSoundAt = nowMs;
      const weaponId = (entity.extra?.['weaponId'] as string | undefined) ?? '';
      const visualStyle = entity.extra?.['visualStyle'] as string | undefined;
      this.audio.playWeaponSound(weaponId, visualStyle);
    });

    this.network.on(MessageType.HitConfirm, (msg) => {
      const hit = msg as HitConfirmMsg;
      const pos = new THREE.Vector3(hit.position.x, hit.position.y, hit.position.z);

      // Recover the weapon-fire cue when the projectile entity never
      // reached the client (point-blank: spawned and destroyed inside a
      // single server tick). The spawn handler tags every projectile we
      // saw on the wire, so a missing id here implies the close-range
      // case and we play the shot sound from the equipped weapon directly.
      const localId = this.state.getLocalPlayerId();
      if (
        localId &&
        hit.attackerId === localId &&
        hit.projectileId &&
        !this.seenProjectileIds.has(hit.projectileId)
      ) {
        const nowMs = performance.now();
        if (nowMs - this.lastWeaponSoundAt >= 45) {
          this.lastWeaponSoundAt = nowMs;
          const equippedId = this.state.getEquipment().equippedWeaponId;
          const weaponDef = WEAPONS[equippedId];
          this.audio.playWeaponSound(equippedId, weaponDef?.visualStyle);
        }
      }

      this.playHitConfirmVfx(hit.targetId, pos, hit.destroyed);

      const target = this.state.getEntity(hit.targetId);
      if (hit.destroyed) {
        if (
          target?.type === EntityType.Asteroid ||
          target?.type === EntityType.NPC ||
          target?.type === EntityType.Ship
        ) {
          this.audio.playImpact('explosion');
        }
        if (target?.type === EntityType.Asteroid) {
          this.tutorial.notifyAsteroidDestroyed();
        } else if (target?.type === EntityType.NPC || target?.type === EntityType.Ship) {
          this.tutorial.notifyNpcKilled();
        }
      }
    });

    this.damageEffectUnsub = this.state.onEntityDamageEffect(
      (e: EntityDamageEffectEvent) => {
        const p = new THREE.Vector3(e.position.x, e.position.y, e.position.z);
        this.playMergedHullDamageVfx(e.entityId, p, e.damage, e.isKill);
      },
    );

    this.network.on(MessageType.DamageEvent, (msg) => {
      const dmg = msg as DamageEventMsg;
      const localId = this.state.getLocalPlayerId();
      if (dmg.targetId === localId) {
        this.hud.triggerDamageFlash();
        this.camera.shake(0.15, 2.5);
        // Shield hit has a brighter metallic ping, hull hit is a dull thud.
        if (dmg.shieldDamage > 0 && dmg.hullDamage <= 0) {
          this.audio.playImpact('shield');
        } else if (dmg.hullDamage > 0) {
          this.audio.playImpact('hull');
        }
        // Per-attacker damage-received chat aggregator (red activity
        // lines, with a 700ms idle / 2.5s max flush window).
        if ((dmg.hullDamage + dmg.shieldDamage) > 0) {
          this.accumulateDamageTaken(dmg);
        }
      }
      if (dmg.shieldDamage > 0) {
        this.entityRenderer.flashShieldOn(dmg.targetId);
      }
    });

    this.network.on(MessageType.XPGain, (msg) => {
      const xp = msg as { amount: number; source?: string };
      // XPGain doubles as the wire shape for two on-death side-effects:
      //   - `source === 'death'` with a negative amount is a real XP
      //     penalty; render in red.
      //   - `source === 'death_xp_saved'` / `'death_cargo_saved'` are
      //     "insurance fired, no loss" toasts. amount is always 0 there
      //     so we render a labelled save instead of "+0 XP".
      if (xp.source === 'death_xp_saved') {
        this.hud.showNotification(t('death.xpPreserved'), 0xffaa44);
      } else if (xp.source === 'death_cargo_saved') {
        this.hud.showNotification(t('death.cargoPreserved'), 0xffaa44);
      } else if (xp.amount < 0) {
        this.hud.showNotification(t('death.xpLost', { amount: -xp.amount }), 0xff5555);
      } else if (xp.source === 'bounty') {
        // Bounty payouts also send a credit delta via InventoryUpdate;
        // we render the XP slice here in gold so the player can tell at
        // a glance "this kill was worth a contract" vs. "regular kill XP".
        this.hud.showNotification(`+${xp.amount} XP — BOUNTY`, 0xffdd44);
      } else if (this.premium.active) {
        // Premium pilots see their 1.5x boost on every gain so the perk
        // stays visible long after purchase.
        this.hud.showNotification(`+${xp.amount} XP ${t('xp.premiumBoostTag')}`, 0x44ff88);
      } else {
        this.hud.showNotification(`+${xp.amount} XP`, 0x44ff88);
        this.maybeShowPremiumXpHint();
      }
    });

    this.network.on(MessageType.LevelUp, (msg) => {
      const lvl = msg as { newLevel: number; maxHp?: number; maxShield?: number; damage?: number };
      this.hud.showLevelUpBanner(lvl.newLevel, {
        maxHp: lvl.maxHp,
        maxShield: lvl.maxShield,
        damage: lvl.damage,
      });
      this.audio.playLevelUp();

      // Self-celebration: golden burst + shockwave rings, anchored to our
      // own ship so it tracks through the animation.
      const self = this.state.getPlayerEntity();
      if (self) {
        const pos = new THREE.Vector3(
          self.transform.position.x,
          self.transform.position.y,
          self.transform.position.z,
        );
        const target = this.entityRenderer.getEntityObject3D(self.id) ?? undefined;
        this.effects.spawnLevelUp(pos, 0xffdd44, target);
      }
    });

    this.network.on(MessageType.PlayerLevelUpFx, (msg) => {
      const fx = msg as { entityId: string; newLevel: number };
      const peer = this.state.getEntity(fx.entityId);
      if (!peer) return;
      const pos = new THREE.Vector3(
        peer.transform.position.x,
        peer.transform.position.y,
        peer.transform.position.z,
      );
      const target = this.entityRenderer.getEntityObject3D(fx.entityId) ?? undefined;
      this.effects.spawnLevelUp(pos, 0xffdd44, target);

      // Quieter variant of the chord stack so nearby level-ups don't drown
      // out combat — reuses the same oscillator recipe at reduced volume.
      this.audio.playPeerLevelUp();

      const label = peer.name?.trim() || 'PILOT';
      this.hud.showPeerLevelUp(label, fx.newLevel);
    });

    this.network.on(MessageType.ResourceCollected, (msg) => {
      const rc = msg as ResourceCollectedMsg;
      const info = RESOURCE_INFO[rc.resource as ResourceTier];
      const color = info?.color ?? 0x00ffaa;
      const name = info?.name ?? 'RESOURCE';
      const playerEntity = this.state.getPlayerEntity();
      if (playerEntity) {
        const pos = new THREE.Vector3(
          playerEntity.transform.position.x,
          playerEntity.transform.position.y,
          playerEntity.transform.position.z,
        );
        this.effects.spawnPickup(pos, color, 15);
      }
      this.hud.showNotification(`+${rc.amount} ${name}`, color);
      this.audio.playPickup();
      this.tutorial.notifyResourceCollected();
    });

    this.network.on(MessageType.InventoryUpdate, (msg) => {
      const inv = msg as InventoryUpdateMsg;
      // If the player just clicked BUY, treat this InventoryUpdate as
      // the server's success ack and play the purchase cue. We don't
      // try to verify the inventory delta — the server already gated
      // the request, and a `buy_failed:` error would have cleared
      // `pendingBuy` before we got here.
      if (this.pendingBuy && performance.now() <= this.pendingBuy.expiresAt) {
        const pb = this.pendingBuy;
        this.pendingBuy = null;
        this.audio.playPurchase();
        this.hud.showNotification(
          t('station.buyConfirmed', {
            name: pb.name.toUpperCase(),
            price: pb.price.toLocaleString(),
          }),
          0x00ff41,
        );
      }
      if (this.pendingCraft && performance.now() <= this.pendingCraft.expiresAt) {
        const pc = this.pendingCraft;
        this.pendingCraft = null;
        this.audio.playUI('confirm');
        this.hud.showNotification(
          t('station.craftConfirmed', { name: pc.name.toUpperCase() }),
          0x44ff88,
        );
      }
      this.hud.updateInventory(inv.inventory, inv.credits);
      this.inventoryPanel.updateData(inv.inventory, inv.credits);
      this.state.updateInventoryState(
        inv.inventory.map((i) => ({
          itemId: i.itemId,
          quantity: i.quantity,
          name: i.itemId,
        })),
        inv.credits,
      );
      if (this.warpGateUI.isVisible()) {
        this.warpGateUI.updatePlayerState({
          discovered: this.state.getDiscoveredSectors(),
          credits: inv.credits,
          claims: this.state.getAllSectorClaims(),
          activeEvents: this.state.getAllActiveSectorEvents(),
        });
      }
      if (this.universeMap.isVisible()) {
        this.universeMap.updatePlayerState({
          current: this.state.getPlayerState().sector,
          discovered: this.state.getDiscoveredSectors(),
          destination: this.state.getNavigationTarget(),
          claims: this.state.getAllSectorClaims(),
          activeEvents: this.state.getAllActiveSectorEvents(),
        });
      }
      if (this.docked) {
        this.stationUI.refresh();
      }
    });

    this.network.on(MessageType.StationShopSnapshot, (msg) => {
      if (!this.docked) return;
      this.stationUI.applyShopSnapshot(msg as StationShopSnapshotMsg);
    });

    // Bounty-board snapshots arrive on auth, on every dock, on every
    // scheduled `bounty_board_refresh` fire, and after a contract is
    // claimed. The cached snapshot is already updated by the network
    // handler in `client.ts`; this listener just forces a re-render of
    // the docked Station UI so the BOUNTIES tab reflects the new state
    // without the player having to click away and back.
    this.network.on(MessageType.BountyBoardData, () => {
      if (this.docked) {
        this.stationUI.refresh();
      }
    });

    // Server-driven loot toast for items rolled off destroyed NPCs.
    // The actual inventory delta arrives separately via InventoryUpdate /
    // EquipmentUpdate; this handler is purely for the celebratory cue.
    this.network.on(MessageType.NpcItemDrop, (msg) => {
      const drop = msg as NpcItemDropMsg;
      const def = WEAPONS[drop.itemId]
        ?? SHIELDS[drop.itemId]
        ?? ENGINES[drop.itemId]
        ?? CONSUMABLES[drop.itemId];
      if (!def) return;
      const kind: 'weapon' | 'shield' | 'engine' | 'consumable' = drop.category;
      const name = itemLabel(kind, drop.itemId, def.name).toUpperCase();
      const isLegendary = (def as { rarity?: string }).rarity === 'legendary';
      const colour = isLegendary ? 0xffaa00 : 0xffff66;
      this.hud.showNotification(
        t('loot.npcItemDrop', { name }),
        colour,
      );
      this.audio.playPickup();
    });

    this.network.on(MessageType.MissionList, (msg) => {
      const m = msg as MissionListMsg;
      // Server sends two scopes over MissionList:
      //   'station' — catalogue at the docked station (only render if docked)
      //   'log'     — the player's active mission log (always relevant for
      //               the journal HUD; can also arrive on join)
      if (m.scope === 'log') {
        this.missionJournal.refresh();
        if (this.docked) this.stationUI.refreshMissionStatus();
        return;
      }
      if (!this.docked) return;
      this.stationUI.applyMissionsFromServer(
        m.missions.map((wm) => ({
          id: wm.id,
          title: wm.title,
          description: wm.description,
          requiredLevel: wm.requiredLevel,
          rewards: { xp: wm.rewards.xp, credits: wm.rewards.credits },
        })),
      );
    });

    this.network.on(MessageType.MissionProgress, () => {
      this.missionJournal.refresh();
    });

    this.network.on(MessageType.MissionComplete, (msg) => {
      const m = msg as MissionCompleteMsg;
      switch (m.outcome) {
        case 'completed': {
          const xp = m.rewards?.xp ?? 0;
          const credits = m.rewards?.credits ?? 0;
          this.hud.showNotification(
            t('mission.completedToast', {
              xp,
              credits: credits.toLocaleString(),
            }),
            0x00ff41,
          );
          this.audio.playUI('confirm');
          break;
        }
        case 'expired':
          this.hud.showNotification(t('mission.expired'), 0xff8800);
          break;
        case 'abandoned':
          this.hud.showNotification(t('mission.abandoned'), 0xff4444);
          break;
        case 'rejected': {
          // The server refused our accept request (cap reached, level
          // too low, already active, …). Show the reason inline as a
          // HUD toast so the player knows why nothing happened, and
          // re-sync the station tab so any optimistic UI clears.
          const reasonKey = m.reason ?? 'mission.reject.notFound';
          this.hud.showNotification(t(reasonKey), 0xff8800);
          this.audio.playUI('error');
          break;
        }
      }
      this.missionJournal.refresh();
      if (this.docked) this.stationUI.refreshMissionStatus();
    });

    this.network.on(MessageType.SectorClaimsSnapshot, () => {
      this.lastSiegePhaseBySector.clear();
      for (const c of this.state.getAllSectorClaims()) {
        this.lastSiegePhaseBySector.set(
          c.sectorKey,
          c.siege?.phase ?? 'idle',
        );
      }
      this.syncMapSectorClaims();
    });

    this.network.on(MessageType.SectorClaimUpdate, (msg) => {
      const c = (msg as SectorClaimUpdateMsg).claim;
      this.lastSiegePhaseBySector.set(c.sectorKey, c.siege?.phase ?? 'idle');
      if (this.docked) {
        this.stationUI.updateTerritory(this.buildTerritoryPanel());
      }
      this.syncMapSectorClaims();
    });

    this.network.on(MessageType.SiegeUpdate, (msg) => {
      const m = msg as SiegeUpdateMsg;
      const prev = this.lastSiegePhaseBySector.get(m.sectorKey);
      this.handleSiegePhaseTransition(m, prev);
      this.lastSiegePhaseBySector.set(m.sectorKey, m.phase);
      this.updateSiegeBanner();
      if (this.docked) {
        this.stationUI.updateTerritory(this.buildTerritoryPanel());
      }
      this.syncMapSectorClaims();
    });

    this.network.on(MessageType.EquipmentUpdate, () => {
      this.inventoryPanel.redrawActiveTab();
      if (this.docked) this.stationUI.refresh();
    });

    this.network.on(MessageType.SectorChange, (msg) => {
      const m = msg as SectorChangeMsg;
      // Surface vs space sector: tear down the old environment and
      // mount the new one. The actual surface mesh is built off the
      // companion `SurfaceData` message (received either right after
      // this one for a fresh land, or never for a space-sector
      // transition).
      if (m.surfaceId) {
        this.onSurface = true;
        this.hud.setOnSurface(true);
        this.camera.setWorldUpOnRoll(true);
        this.state.setOnSurface(true);
      } else if (this.onSurface) {
        this.onSurface = false;
        this.hud.setOnSurface(false);
        this.camera.setWorldUpOnRoll(false);
        this.surfaceRenderer.unload();
        this.state.setOnSurface(false);
      }
      if (this.universeMap.isVisible()) {
        this.universeMap.updatePlayerState({
          current: { ...m.sector },
          discovered: this.state.getDiscoveredSectors(),
          destination: this.state.getNavigationTarget(),
          claims: this.state.getAllSectorClaims(),
          activeEvents: this.state.getAllActiveSectorEvents(),
        });
      }
    });

    this.network.on(MessageType.SectorDiscovered, (msg) => {
      const m = msg as SectorDiscoveredMsg;
      const key = `[${m.sector.x}, ${m.sector.y}, ${m.sector.z}]`;
      this.hud.showNotification(
        `${m.isFirstEver ? 'FIRST DISCOVERY! ' : ''}NEW SECTOR DISCOVERED — ${key}`,
        m.isFirstEver ? 0xffdd44 : 0x44ccff,
      );
      if (this.warpGateUI.isVisible()) {
        this.warpGateUI.updatePlayerState({
          discovered: this.state.getDiscoveredSectors(),
          credits: this.state.getPlayerState().credits,
          claims: this.state.getAllSectorClaims(),
          activeEvents: this.state.getAllActiveSectorEvents(),
        });
      }
      if (this.universeMap.isVisible()) {
        this.universeMap.updatePlayerState({
          current: this.state.getPlayerState().sector,
          discovered: this.state.getDiscoveredSectors(),
          destination: this.state.getNavigationTarget(),
          claims: this.state.getAllSectorClaims(),
          activeEvents: this.state.getAllActiveSectorEvents(),
        });
      }
    });

    // The maps subscribe to live event updates so a fresh mothership
    // alert / raid lights up the indicator without the player having
    // to close and reopen the panel. We piggy-back on the same sync
    // helper used for claims to keep the call shape uniform.
    this.network.on(MessageType.SectorEventsSnapshot, () => {
      this.syncMapWorldState();
    });
    this.network.on(MessageType.SectorEventUpdate, () => {
      this.syncMapWorldState();
    });

    this.network.on(MessageType.DeathEvent, (msg) => {
      const death = msg as DeathEventMsg;
      const localId = this.state.getLocalPlayerId();
      if (localId && death.entityId === localId) {
        this.hud.showNotification('SHIP DESTROYED', 0xff3344);
      }
    });

    // Server-authoritative onboarding phase. The first message arrives
    // right after JoinGame for new pilots (dumping them into their
    // private tutorial sector); subsequent messages walk them through
    // movement → mining → combat. `'done'` is the final echo and tells
    // the overlay to tear itself down while the server warps the
    // pilot to sector 0,0,0.
    this.network.on(MessageType.TutorialPhase, (msg) => {
      const m = msg as TutorialPhaseMsg;
      if (m.phase === 'done') {
        this.tutorial.hide();
        this.controls.showHint();
      } else {
        this.tutorial.setPhase(m.phase);
      }
    });

    this.network.on(MessageType.RespawnConfirm, (msg) => {
      const r = msg as RespawnConfirmMsg;
      const key = `[${r.sector.x}, ${r.sector.y}, ${r.sector.z}]`;
      this.hud.showNotification(`RESPAWNED AT STATION — ${key}`, 0x44ff88);
      if (this.warpGateUI.isVisible()) {
        this.closeWarpGateUI();
      }
      if (this.docked) {
        this.docked = false;
        this.stationUI.hide();
      }
      // Drop any unflushed damage tallies from the previous life so
      // the first hit after respawn doesn't get glued onto a stale
      // attacker bucket.
      this.resetDamageAggregator();
      this.input.enable();
    });

    this.network.on(MessageType.WarpTravelResult, (msg) => {
      const result = msg as WarpTravelResultMsg;
      if (result.success && result.sector) {
        const key = `[${result.sector.x}, ${result.sector.y}, ${result.sector.z}]`;
        this.hud.showNotification(
          `WARP → ${key}  (−${result.cost ?? 0}C)`,
          0x00ccff,
        );
        this.closeWarpGateUI();
      } else {
        const errorMsg = this.translateWarpError(result.error);
        this.hud.showNotification(errorMsg, 0xff4444);
        if (this.warpGateUI.isVisible()) {
          this.warpGateUI.setError(errorMsg);
        }
      }
    });

    // ─── Planetary landing ───
    this.network.on(MessageType.SurfaceData, (msg) => {
      const m = msg as SurfaceDataMsg;
      console.info(
        `[surface] got SurfaceData id=${m.surface.id} ` +
        `name="${m.surface.name}" biome=${m.surface.biome} ` +
        `hm=${m.surface.heightmap.length} bases=${m.surface.bases.length} ` +
        `npcs=${m.surface.npcs.length}`,
      );
      this.surfaceRenderer.load(m.surface);
      // Tell the HUD the live planet name so the zone label reads
      // "ATLAS PRIME" instead of the parent sector's "DEEP VOID"
      // tier. Cleared on liftoff via setOnSurface(false).
      this.hud.setActiveSurfaceName(m.surface.name);
      // Hand the predictor a heightmap sampler so the local ship
      // visually rides the terrain between server snapshots —
      // without this the client predicts a flat-ground hover and
      // the camera bobs as each reconcile snaps Y back onto the
      // hill / valley underneath.
      this.state.setSurfaceHeightSampler((x, z) =>
        this.surfaceRenderer.sampleHeightAt(x, z),
      );
      // Tell the predictor the playable half-width so it mirrors
      // the server's hard X/Z boundary clamp. Without this the
      // local ship sails past the rim wall in prediction, the
      // server snaps it back at the boundary, and the user sees
      // a "stuck against the wall" stutter every time they fly
      // into the perimeter.
      this.state.setSurfaceHalfSize(m.surface.size / 2);
      // Hand the predictor the same flora collider list the server
      // uses (Tree / Mushroom / IcePillar / Crystal / Boulder
      // cylinders), so the local prediction stops the ship at trees
      // exactly when the server does. Without this the player flies
      // straight through trees in prediction, the server bounces
      // them back, and they see a sharp reconcile snap on every
      // tree contact.
      this.state.setSurfaceFloraColliders(collectFloraColliders(m.surface));
      // Pre-compile every shader currently in the scene against the
      // *new* light environment (the surface adds a HemisphereLight
      // and the bases ship more emissive materials). Without this
      // warm-up Three.js compiles each material lazily on its first
      // render — so the player would see one shader-compile stall
      // every time they pan the camera and a new base / tank /
      // flora cluster came into the frustum, which is exactly the
      // "freezes when I move" stutter the user reported.
      // Cheap on a desktop GPU (<50 ms once) and hidden by the
      // landing transition. We schedule it for the next frame so
      // the freshly-mounted surface group is already in the scene
      // graph when compile() walks it.
      // Two-pass pre-compile:
      //   1) Next frame: compiles the surface group + every entity
      //      already present in the scene at land time (heightmap,
      //      sky, hemilight, mountain ring, flora, plus the bases /
      //      NPCs that arrived in the same EntityUpdate the
      //      SurfaceData rode in with).
      //   2) Two seconds later: compiles anything that was added
      //      between "land time" and "the surface settled" — NPC
      //      respawns, slow-arriving entity batches on a flaky
      //      connection, and any debris / pickups from an immediate
      //      combat encounter. We schedule via setTimeout instead of
      //      a frame hook so the second pass happens during the
      //      relatively quiet window after the landing transition,
      //      not during the first crowded frame the player sees.
      // ASYNC pre-compile. The synchronous `renderer.compile()` we
      // were calling here was *itself* the source of one of the
      // user-reported 3+ second freezes: the call walks every
      // material in the scene and compiles each shader on the main
      // thread, blocking for as long as the GPU driver takes to
      // build the program (which on a fresh land with bases + NPCs
      // + flora can be multiple seconds). `compileAsync` returns a
      // Promise that resolves when the GPU is done, with the actual
      // compile happening on the GPU's parallel-compile pipeline so
      // the frame loop keeps ticking. Falls back gracefully if the
      // renderer doesn't expose it (e.g. a Three.js version older
      // than r152).
      const precompile = async (label: string) => {
        try {
          const r = this.renderer.getRenderer();
          const scene = this.scene.getScene();
          const camera = this.camera.getCamera();
          if (typeof r.compileAsync === 'function') {
            await r.compileAsync(scene, camera);
          } else {
            r.compile(scene, camera);
          }
        } catch (err) {
          console.warn(`[surface] shader pre-compile (${label}) failed:`, err);
        }
      };
      requestAnimationFrame(() => { void precompile('first-frame'); });
      window.setTimeout(() => { void precompile('settled'); }, 2000);
    });

    this.network.on(MessageType.LandResult, (msg) => {
      const r = msg as LandResultMsg;
      if (!r.success) {
        if (r.error === 'planet_premium_only') {
          // Server-authoritative premium gate — reached when the client
          // prediction missed (e.g. stale entity flag). Same UX as the
          // client-side short-circuit: notify + open the upgrade modal.
          this.hud.showNotification(t('planet.premiumOnly'), 0xffd700);
          this.openUpgradeModal('planet');
          return;
        }
        const reason = r.error ? `LAND FAILED — ${r.error.toUpperCase()}` : 'LAND FAILED';
        this.hud.showNotification(reason, 0xff4444);
        return;
      }
      this.hud.showNotification(`LANDING — ${r.surfaceName ?? 'PLANET'}`, 0xffaa44);
    });

    this.network.on(MessageType.LiftoffResult, (msg) => {
      const r = msg as LiftoffResultMsg;
      if (!r.success) {
        const reason = r.error ? `LIFTOFF FAILED — ${r.error.toUpperCase()}` : 'LIFTOFF FAILED';
        this.hud.showNotification(reason, 0xff4444);
        return;
      }
      this.hud.showNotification('LIFTOFF', 0x44ff88);
    });

    this.network.on(MessageType.InvestigateRuinResult, (msg) => {
      const r = msg as InvestigateRuinResultMsg;
      if (!r.success) {
        const reason = r.error
          ? `INVESTIGATE FAILED — ${r.error.toUpperCase()}`
          : 'INVESTIGATE FAILED';
        this.hud.showNotification(reason, 0xff4444);
        return;
      }
      this.hud.showNotification('SITE INVESTIGATED', 0x88e0ff);
    });

    this.network.on(MessageType.CodexUnlock, (msg) => {
      const r = msg as CodexUnlockMsg;
      if (!r.ids || r.ids.length === 0) return;
      let added = 0;
      for (const id of r.ids) {
        if (!this._unlockedCodex.has(id)) {
          this._unlockedCodex.add(id);
          added++;
        }
      }
      // Only toast the newly-discovered fragments — the post-login
      // hydration snapshot of every previously-unlocked id arrives
      // as a single CodexUnlock and would otherwise spam the UI with
      // "CODEX UPDATED" toasts for everything the pilot already had.
      if (added === r.ids.length) {
        for (const id of r.ids) {
          this.hud.showNotification(`CODEX UPDATED — ${id.toUpperCase()}`, 0xc88dff);
        }
      }
      if (this.docked) {
        this.stationUI.refresh();
      }
    });

    this.network.on(MessageType.SurfaceWeather, (msg) => {
      const r = msg as SurfaceWeatherMsg;
      const remainingMs = Math.max(0, r.endsAtMs - Date.now());
      this.hud.showSurfaceWeather({ kind: r.kind, remainingMs });
    });

    this.network.on(MessageType.PlanetReputationUpdate, (msg) => {
      const r = msg as PlanetReputationUpdateMsg;
      this._planetReputation = new Map(Object.entries(r.reputation ?? {}));
      if (r.delta) {
        const sign = r.delta.amount > 0 ? '+' : '';
        this.hud.showNotification(
          `REPUTATION ${sign}${r.delta.amount}`,
          r.delta.amount > 0 ? 0x88e0ff : 0xff8866,
        );
      }
      if (this.docked) this.stationUI.refresh();
    });

    this.network.on(MessageType.LiberatedSurfaces, (msg) => {
      const r = msg as LiberatedSurfacesMsg;
      this._liberatedSurfaces = new Set(r.surfaceIds ?? []);
      if (this.docked) this.stationUI.refresh();
    });

    this.network.on(MessageType.ChatReceive, (msg) => {
      const chat = msg as ChatReceiveMsg;
      this.chat.addIncoming({
        senderId: chat.senderId,
        senderName: chat.senderName,
        channel: chat.channel,
        content: chat.content,
        timestamp: chat.timestamp,
        targetId: chat.targetId,
        targetName: chat.targetName,
        localPlayerId: this.state.getLocalPlayerId() ?? undefined,
        premium: chat.premium === true,
      });
      this.chatWindow.addIncoming({
        senderId: chat.senderId,
        senderName: chat.senderName,
        channel: chat.channel,
        content: chat.content,
        timestamp: chat.timestamp,
        targetName: chat.targetName,
        localPlayerId: this.state.getLocalPlayerId() ?? undefined,
        premium: chat.premium === true,
      });
      if (
        chat.channel === ChatChannel.Direct &&
        chat.senderName.toLowerCase() !== character.callsign.toLowerCase()
      ) {
        this.hud.showNotification(
          `DM · ${chat.senderName}: ${chat.content.slice(0, 40)}`,
          0xff88ff,
        );
      }
    });

    this.network.on(MessageType.Error, (msg) => {
      const err = msg as ErrorMsg;
      // Server kicked us because the same account logged in from another
      // device. Stash the reason so handleServerDisconnect (which fires
      // when the WS close arrives a few ms later) can show a meaningful
      // banner instead of the generic "Disconnected" copy.
      if (typeof err.message === 'string' && err.message === 'kicked:other_login') {
        this.pendingDisconnectReason = 'kicked';
      }
      if (typeof err.message === 'string' && err.message.startsWith('chat_')) {
        this.chat.addSystemFromServer(err.message);
      }
      // Surface consumable-use failures as an HUD notification so a user
      // who spammed USE on a full-HP ship gets a tactile "nothing happened"
      // instead of the UI silently eating the click.
      if (typeof err.message === 'string' && err.message.startsWith('use_failed:')) {
        const reason = err.message.slice('use_failed:'.length);
        const msgText =
          reason === 'full_hp' ? t('consumable.noNeedFullHp')
          : reason === 'full_shield' ? t('consumable.noNeedFullShield')
          : reason === 'docked' ? t('consumable.cantUseDocked')
          : err.message;
        this.hud.showNotification(msgText, 0xffaa00);
      }
      // Surface equipment sell rejections from the server so the user knows
      // why their click did nothing (e.g. the item they picked is actually
      // equipped right now, or the pack has been partially drained).
      if (typeof err.message === 'string' && err.message.startsWith('sell_failed:')) {
        const reason = err.message.slice('sell_failed:'.length);
        const msgText =
          reason === 'equipped' ? t('station.sellFailedEquipped')
          : reason === 'not_unused' ? t('station.sellFailedUsed')
          : t('station.sellFailed');
        this.hud.showNotification(msgText, 0xffaa00);
      }
      if (typeof err.message === 'string' && err.message.startsWith('buy_failed:')) {
        const reason = err.message.slice('buy_failed:'.length);
        const msgText =
          reason === 'insufficient_credits' ? t('station.buyFailedCredits')
          : reason === 'level_too_low' ? t('station.buyFailedLevel')
          : reason === 'already_owned' ? t('station.buyFailedAlreadyOwned')
          : reason === 'not_stocked_here' ? t('station.buyFailedStock')
          : reason === 'not_docked' ? t('station.buyFailedNotDocked')
          : t('station.buyFailed');
        this.pendingBuy = null;
        this.audio.playUI('error');
        this.hud.showNotification(msgText, 0xffaa00);
      }
      if (typeof err.message === 'string' && err.message.startsWith('craft_failed:')) {
        const reason = err.message.slice('craft_failed:'.length);
        const msgText =
          reason === 'not_docked' ? t('station.craftFailedNotDocked')
          : reason === 'level_too_low' ? t('station.craftFailedLevel')
          : reason === 'missing_resources' ? t('station.craftFailedResources')
          : reason === 'not_craftable' || reason === 'unknown_item'
            ? t('station.craftFailedUnknown')
          : t('station.craftFailed');
        this.pendingCraft = null;
        this.audio.playUI('error');
        this.hud.showNotification(msgText, 0xffaa00);
      }
      if (typeof err.message === 'string' && err.message.startsWith('buy_ship_failed:')) {
        const reason = err.message.slice('buy_ship_failed:'.length);
        const msgText =
          reason === 'not_docked' ? t('hangar.buyFailedNotDocked')
          : reason === 'already_owned' ? t('hangar.buyFailedAlreadyOwned')
          : reason === 'level_too_low' ? t('hangar.levelTooLow')
          : reason === 'cannot_afford' ? t('hangar.cannotAfford')
          : t('hangar.buyFailed');
        this.hud.showNotification(msgText, 0xffaa00);
      }
      if (typeof err.message === 'string' && err.message.startsWith('switch_ship_failed:')) {
        const reason = err.message.slice('switch_ship_failed:'.length);
        const msgText =
          reason === 'not_docked' ? t('hangar.switchFailedNotDocked')
          : reason === 'not_owned' ? t('hangar.switchFailedNotOwned')
          : reason === 'already_active' ? t('hangar.switchFailedAlreadyActive')
          : t('hangar.switchFailed');
        this.hud.showNotification(msgText, 0xffaa00);
      }
    });

    this.network.on(MessageType.SystemAnnouncement, (msg) => {
      const ann = msg as SystemAnnouncementMsg;
      // Banner + HUD notification. The server also fires a ChatReceive on
      // the system channel so the chat log shows the text for players who
      // are looking away from the HUD banner when it flashes.
      const text = `${ann.title} — ${ann.body}`;
      const level: 'info' | 'warning' | 'danger' | 'success' =
        ann.level === 'info' || ann.level === 'warning'
          || ann.level === 'danger' || ann.level === 'success'
          ? ann.level
          : 'warning';
      this.hud.showNotification(text, level);
    });

    this.network.on(MessageType.ShipHangarUpdate, (msg) => {
      const update = msg as ShipHangarUpdateMsg;
      if (update.cosmetics) {
        this.shipCosmetics = { ...update.cosmetics };
      }
      if (this.docked) this.stationUI.refresh();
    });

    this.network.on(MessageType.ShipCosmeticsUpdate, (msg) => {
      const update = msg as ShipCosmeticsUpdateMsg;
      this.shipCosmetics = { ...update.cosmetics };
      if (!update.applied) {
        // Server clamped the request — most commonly because a free
        // pilot tried to equip a premium pattern. Surface a toast so
        // the user understands why the change didn't stick. The
        // picker UI itself was already closed on optimistic Apply;
        // re-opening it is up to the user.
        const reasonKey = update.reason === 'premium_required'
          ? 'hangar.customizePremiumNeeded'
          : 'hangar.customizeInvalid';
        this.hud?.showNotification?.(t(reasonKey), 0xffaa00);
      } else if (update.cosmetics.pattern === DEFAULT_SHIP_COSMETICS.pattern
          && update.cosmetics.secondaryColor === DEFAULT_SHIP_COSMETICS.secondaryColor) {
        this.hud?.showNotification?.(t('hangar.customizeResetDone'), 0x00ff88);
      } else {
        this.hud?.showNotification?.(t('hangar.customizeApplied'), 0x00ff88);
      }
      if (this.docked) this.stationUI.refresh();
    });

    this.network.on(MessageType.MerchantOffer, (msg) => {
      this.showMerchantModal(msg as MerchantOfferMsg);
    });

    this.network.on(MessageType.LeaderboardData, (msg) => {
      const data = msg as LeaderboardDataMsg;
      this.leaderboard.setData(
        data.board,
        data.entries,
        data.selfRank,
        data.generatedAt,
      );
    });

    this.network.on(MessageType.FriendsListData, () => {
      this.friendsPanel.refresh();
      this.chatWindow.refreshFriends();
    });
    this.network.on(MessageType.FriendRequestsData, () => {
      this.friendsPanel.refresh();
    });
    this.network.on(MessageType.ChatHistoryData, (msg) => {
      this.chatWindow.applyHistory(msg as ChatHistoryDataMsg);
    });
    this.network.on(MessageType.PlayerProfileData, (msg) => {
      this.chatWindow.applyProfile(msg as PlayerProfileDataMsg);
    });
    this.network.on(MessageType.FriendStatusUpdate, (msg) => {
      const m = msg as FriendStatusUpdateMsg;
      this.friendsPanel.refresh();
      const key = m.online ? 'friends.toastOnline' : 'friends.toastOffline';
      this.hud.showNotification(t(key, { name: m.friendName }), m.online ? 0x00ff41 : 0xffb000);
    });
    this.network.on(MessageType.FriendRequestReceived, (msg) => {
      const m = msg as FriendRequestReceivedMsg;
      this.friendsPanel.refresh();
      this.hud.showNotification(
        t('friends.toastRequestFrom', { name: m.request.fromName }),
        0xffb000,
      );
      this.audio.playUI('notification');
    });
    this.network.on(MessageType.TradeResponse, (msg) => {
      const m = msg as TradeResponseMsg;
      const itemName = this.friendlyItemLabel(m.itemId);
      if (m.outcome === 'received') {
        this.hud.showNotification(
          t('trade.toastReceived', {
            name: m.fromName ?? '?',
            qty: m.quantity,
            item: itemName,
          }),
          0x00ff41,
        );
        this.audio.playUI('notification');
      } else if (m.outcome === 'sent') {
        this.hud.showNotification(
          t('trade.toastSent', {
            name: m.toName ?? '?',
            qty: m.quantity,
            item: itemName,
          }),
          0xffb000,
        );
        this.audio.playUI('confirm');
      } else {
        const reason = m.reason ? t(m.reason) : t('trade.toastFailed');
        this.hud.showNotification(reason, 0xff4444);
        this.audio.playUI('error');
      }
    });
    this.network.on(MessageType.MarketplaceListings, () => {
      this.marketplacePanel.refresh();
    });
    this.network.on(MessageType.MarketplaceResult, (msg) => {
      const m = msg as MarketplaceResultMsg;
      const ok = m.outcome !== 'failed';
      const key = ok
        ? `marketplace.toast.${m.outcome}`
        : (m.reason ?? 'marketplace.toast.failed');
      const text = t(key);
      this.marketplacePanel.setStatus(text, ok);
      this.hud.showNotification(text, ok ? 0x00ff41 : 0xff4444);
      this.audio.playUI(ok ? 'confirm' : 'error');
      if (ok) {
        // Cancel/buy/create all change the visible listing set — pull
        // fresh snapshots so the panel doesn't show stale rows.
        this.network.sendMarketplaceList('all');
        this.network.sendMarketplaceList('mine');
      }
    });
    this.network.on(MessageType.MarketplaceSold, (msg) => {
      const m = msg as MarketplaceSoldMsg;
      const itemName = this.friendlyItemLabel(m.itemId);
      this.hud.showNotification(
        t('marketplace.toast.sold', {
          name: m.buyerName,
          qty: m.quantity,
          item: itemName,
          credits: m.totalPrice.toLocaleString(),
        }),
        0xffb000,
      );
      this.audio.playUI('notification');
      this.network.sendMarketplaceList('mine');
    });

    this.network.on(MessageType.ActiveBuffsUpdate, (msg) => {
      const buffs = (msg as ActiveBuffsUpdateMsg).buffs;
      // We deliberately only surface new / refreshed buffs, not expirations
      // — expirations arrive as an empty list and spamming the HUD on each
      // tick would be noisy. The StationUI / inventory panel both redraw
      // on the next InventoryUpdate so quantity stays in sync separately.
      if (buffs.length === 0) return;
      for (const buff of buffs) {
        const kindLabel = buff.kind === 'thrust'
          ? t('consumable.buffThrust')
          : t('consumable.buffDamage');
        const pct = Math.round((buff.multiplier - 1) * 100);
        const seconds = Math.max(0, Math.round((buff.expiresAt - Date.now()) / 1000));
        this.hud.showNotification(
          `${kindLabel}  +${pct}%  (${seconds}s)`,
          buff.kind === 'thrust' ? 0x44ccff : 0xff8844,
        );
      }
    });

    this.chat.setLocalPlayer(character.callsign);
    this.chatWindow.setLocalPlayer(character.callsign);
    this.marketplacePanel.setLocalKey(character.callsign);
    this.chat.sendMessage = (content, channel, targetName) => {
      if (this.handleClientSlashCommand(content)) {
        // Admin perf snapshots: print a local line *and* still forward
        // to the server so the GM handler returns its own snapshot.
        // Keeps `/perf` a single round-trip from the user's POV.
      }
      if (channel === ChatChannel.Direct) {
        this.network.sendChat(channel, content, { targetName });
      } else {
        this.network.sendChat(channel, content);
      }
    };
    this.chat.show();
    this.startPerfMonitorIfAdmin();

    this.input.enable();
    this.hud.show();
    this.touchControls?.show();
    this.touchControls?.setGameplayButtonsVisible(true);
    this.running = true;
    this.lastTime = performance.now();
    this.loop(this.lastTime);

    // Tutorial visibility is now server-driven — the server emits
    // `TutorialPhase` after JoinGame for new pilots, which is the only
    // thing that shows the onboarding overlay. Experienced pilots
    // (returning characters) never receive that message and immediately
    // see the regular controls hint.
    if (!isFirstTime) {
      this.controls.showHint();
    }
  }

  private showConnectionError(
    character: SavedCharacter,
    isFirstTime: boolean,
  ): void {
    const hudContainer = document.getElementById('hud')!;

    if (this.connectErrorEl) this.connectErrorEl.remove();

    this.connectErrorEl = document.createElement('div');
    Object.assign(this.connectErrorEl.style, {
      position: 'absolute',
      inset: '0',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '20px',
      background: 'rgba(0,8,0,0.95)',
      fontFamily: "'Share Tech Mono', monospace",
      color: '#00ff41',
      textTransform: 'uppercase',
      letterSpacing: '2px',
      zIndex: '300',
    });

    const icon = document.createElement('div');
    Object.assign(icon.style, {
      fontSize: '48px',
      opacity: '0.6',
    });
    icon.textContent = '⚠';
    this.connectErrorEl.appendChild(icon);

    const title = document.createElement('div');
    Object.assign(title.style, {
      fontSize: 'clamp(16px, 2.5vw, 24px)',
      textShadow: '0 0 8px rgba(255,68,68,0.5)',
      color: '#ff4444',
      letterSpacing: '4px',
    });
    title.textContent = t('connError.title');
    this.connectErrorEl.appendChild(title);

    const msg = document.createElement('div');
    Object.assign(msg.style, {
      fontSize: '12px',
      color: 'rgba(0,255,65,0.6)',
      textAlign: 'center',
      maxWidth: '400px',
      lineHeight: '1.6',
      textTransform: 'none',
    });
    msg.textContent = t('connError.message');
    this.connectErrorEl.appendChild(msg);

    const btnRow = document.createElement('div');
    Object.assign(btnRow.style, {
      display: 'flex',
      gap: '16px',
      marginTop: '8px',
    });

    const retryBtn = document.createElement('button');
    Object.assign(retryBtn.style, {
      fontFamily: "'Share Tech Mono', monospace",
      fontSize: '14px',
      padding: '12px 36px',
      background: 'transparent',
      border: '2px solid #00ff41',
      color: '#00ff41',
      cursor: 'pointer',
      textTransform: 'uppercase',
      letterSpacing: '4px',
      transition: 'all 0.2s',
      pointerEvents: 'auto',
    });
    retryBtn.textContent = t('connError.retry');
    retryBtn.addEventListener('mouseenter', () => {
      retryBtn.style.background = '#00ff41';
      retryBtn.style.color = '#000';
    });
    retryBtn.addEventListener('mouseleave', () => {
      retryBtn.style.background = 'transparent';
      retryBtn.style.color = '#00ff41';
    });
    retryBtn.addEventListener('click', () => {
      this.connectErrorEl?.remove();
      this.connectErrorEl = null;
      this.startGame(character, isFirstTime);
    });
    btnRow.appendChild(retryBtn);

    const backBtn = document.createElement('button');
    Object.assign(backBtn.style, {
      fontFamily: "'Share Tech Mono', monospace",
      fontSize: '12px',
      padding: '12px 24px',
      background: 'transparent',
      border: '1px solid rgba(0,255,65,0.3)',
      color: 'rgba(0,255,65,0.6)',
      cursor: 'pointer',
      textTransform: 'uppercase',
      letterSpacing: '2px',
      transition: 'all 0.2s',
      pointerEvents: 'auto',
    });
    backBtn.textContent = t('connError.goBack');
    backBtn.addEventListener('mouseenter', () => {
      backBtn.style.borderColor = '#00ff41';
      backBtn.style.color = '#00ff41';
    });
    backBtn.addEventListener('mouseleave', () => {
      backBtn.style.borderColor = 'rgba(0,255,65,0.3)';
      backBtn.style.color = 'rgba(0,255,65,0.6)';
    });
    backBtn.addEventListener('click', () => {
      this.connectErrorEl?.remove();
      this.connectErrorEl = null;
      if (this.serverCharacters.length > 0) {
        this.showCharSelect(this.serverCharacters);
      } else {
        this.showCharCreate();
      }
    });
    btnRow.appendChild(backBtn);

    this.connectErrorEl.appendChild(btnRow);
    hudContainer.appendChild(this.connectErrorEl);
  }

  private loop = (time: number) => {
    if (!this.running) return;
    requestAnimationFrame(this.loop);

    const dt = Math.min((time - this.lastTime) / 1000, 0.1);
    this.lastTime = time;

    // ── Freeze profiler ────────────────────────────────────────────
    // The user-reported "ship freezes for a moment, then snaps to
    // expected destination" symptom is hard to debug from console
    // logs alone — by the time the user notices a freeze, dozens of
    // frames have already passed. We instead instrument every major
    // section of the loop and *only* dump the breakdown when this
    // frame's wall-clock time exceeds the freeze threshold (50 ms).
    // The output names every section that took > 5 ms so the
    // dominant cost is obvious from the console.
    //
    // Also tracks "predictor stall": when the local player's X/Z
    // hasn't advanced in N consecutive frames despite forward
    // input being held — that's the on-screen freeze from the
    // user's POV even when the frame itself is fast.
    // Catch sub-jank too. Anything past 30 ms is already *visibly*
    // a missed frame on a 60 Hz display; at 50 ms (the old cap) the
    // user has already felt the stutter. Pairs with the 5 ms section
    // floor below so the breakdown still skips noise from the dozens
    // of ≤ 5 ms helper sections each frame runs through.
    const FREEZE_FRAME_MS = 30;
    const SECTION_LOG_MS = 5;
    const frameStart = performance.now();
    const sectionTimes: Record<string, number> = {};
    let lastMark = frameStart;
    const mark = (name: string): void => {
      const now = performance.now();
      const elapsed = now - lastMark;
      sectionTimes[name] = (sectionTimes[name] ?? 0) + elapsed;
      lastMark = now;
    };

    const playerBefore = this.state.getPlayerEntity();
    const xBefore = playerBefore?.transform.position.x ?? 0;
    const zBefore = playerBefore?.transform.position.z ?? 0;
    const onSurfaceBefore = !!(this.state as unknown as { _onSurface: boolean })._onSurface;

    this.input.update();
    mark('input.update');

    const inputState = this.input.getState();
    this.applyMobileAimAssist(inputState, dt);
    this.network.sendInput(inputState);
    mark('input.send');

    this.state.applyLocalInput(inputState, dt);
    mark('predict');
    // Drive the onboarding overlay: it ticks off movement chips as
    // the pilot tries each thrust direction during the first phase.
    this.tutorial.observeInput(inputState);

    this.state.interpolateEntities(dt);
    mark('interp');

    this.entityRenderer.update(this.state);
    mark('entityRender');
    this.effects.update(dt);
    mark('fx');

    // ── Lazy shader-compile guard ──────────────────────────────────
    // Three.js compiles a material's shader on its FIRST render,
    // synchronously, on the main thread. With the EffectComposer
    // pipeline (RenderPass + UnrealBloomPass + ScanlineShader) a
    // single new material costs THREE shader compiles — easily 100+
    // ms for a complex MeshStandardMaterial — and the user sees
    // that as a brief "ship freezes for a beat then snaps to where
    // it should have been" stall every time a new NPC respawns,
    // a projectile with a yet-unseen weapon style fires, or a
    // freshly-loaded LOD swaps in.
    //
    // The fix: when entity-renderer reports newly-created visuals,
    // schedule ONE async compile pass to warm those shaders before
    // the renderer tries to draw them. We coalesce bursts (NPC
    // wave spawning all at once) into a single compile via a
    // pending RAF handle, and prefer `compileAsync` so the GPU
    // does the work on its parallel-compile queue instead of
    // blocking the next frame.
    const newVisuals = this.entityRenderer.consumeVisualsCreatedCount();
    if (newVisuals > 0 && this._pendingCompileRaf == null) {
      this._pendingCompileRaf = requestAnimationFrame(() => {
        this._pendingCompileRaf = null;
        try {
          const r = this.renderer.getRenderer();
          const scene = this.scene.getScene();
          const camera = this.camera.getCamera();
          if (typeof r.compileAsync === 'function') {
            void r.compileAsync(scene, camera);
          }
          // No sync `compile()` fallback here — for the per-entity
          // hot path the cost of a full sync walk is exactly the
          // freeze we're trying to prevent. Three's lazy compile
          // will still eventually catch any missed material; the
          // async path just takes the spike off the main thread.
        } catch (err) {
          console.warn('[render] async pre-compile failed:', err);
        }
      });
    }

    const playerShip = this.state.getPlayerEntity();
    if (playerShip) {
      const pos = playerShip.transform.position;
      const rot = playerShip.transform.rotation;
      // Keep the chase camera framed for the active hull's actual size
      // — without this, every Cruiser+ pilot sees the camera embedded
      // in the hull because the offset was tuned for the Fighter.
      // `playerShip.shipClass` is the numeric `ShipClass` enum from the
      // wire format; the visual scale table is keyed by the lowercase
      // slug, so we route through `SHIP_CLASS_KEY` to bridge them.
      const scClass = playerShip.shipClass;
      let shipScale: number | undefined;
      if (typeof scClass === 'string') {
        shipScale = SHIP_CLASS_VISUAL_SCALE[scClass];
      } else if (typeof scClass === 'number') {
        const slug = (SHIP_CLASS_KEY as Record<number, string>)[scClass];
        if (slug) shipScale = SHIP_CLASS_VISUAL_SCALE[slug];
      }
      this.camera.setShipScale(shipScale ?? 3.2);
      this._camTargetVec.set(pos.x, pos.y, pos.z);
      this._camTargetEuler.set(rot.x, rot.y, rot.z, 'YXZ');
      this._camTargetQuat.setFromEuler(this._camTargetEuler);
      this.camera.update(this._camTargetIO, dt);

      // Drive engine audio from actual ship speed so throttle-down also
      // eases the hum back down. setEngineSpeed itself uses setTargetAtTime
      // internally, giving us a smooth crossfade.
      const v = playerShip.transform.velocity;
      const speed = Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z);
      // ~80 u/s is a brisk cruise, ~120 u/s with boost — clamp to 1.
      this.audio.setEngineSpeed(Math.min(1, speed / 80));
    } else {
      this.audio.setEngineSpeed(0);
    }

    this.scene.update(dt, this.camera.getCamera().position);
    mark('scene');

    this.hud.update(this.state, this.input);
    this.hud.updateDamageOverlay(dt);
    this.updateSiegeBanner();
    mark('hud');
    if (this.inventoryPanel.visible) {
      // Keep the credits counter (and any live-derived numbers) in
      // lockstep with the HUD while the panel is open. `refreshNow` is a
      // cheap DOM text update when nothing changed.
      this.inventoryPanel.refreshNow();
    }

    const cam = this.camera.getCamera();
    this.worldLabels.update(
      this.state,
      cam,
      window.innerWidth,
      window.innerHeight,
    );
    mark('labels');

    const target = this.worldLabels.currentTarget;
    this.hud.updateTargetInfo(target?.entity ?? null, target?.distance ?? 0);

    // Auto-fire (mobile): when the pilot has toggled the FIRE pill to
    // AUTO, we pull the trigger on their behalf whenever a valid
    // crosshair target sits inside the equipped weapon's range.
    // "Valid" = hostile/targetable entity type (NPC, other ship,
    // asteroid). Stars/stations/planets/gates/resources are skipped
    // so the pilot can't accidentally open fire on the warp gate they
    // were lining up. If no weapon is equipped, or the target is out
    // of range, we clear the active flag so the next shot is the
    // pilot's choice again rather than a stale trigger-hold.
    if (this.touchControls?.isAutoFireEnabled()) {
      let fireNow = false;
      if (target && !this.docked && !this.paused) {
        const kind = target.entity.type;
        const targetable =
          kind === EntityType.NPC ||
          kind === EntityType.Ship ||
          kind === EntityType.Asteroid;
        if (targetable) {
          const eq = this.state.getEquipment();
          const weapon = eq?.equippedWeaponId ? WEAPONS[eq.equippedWeaponId] : undefined;
          const range = weapon?.range ?? 0;
          if (range > 0 && target.distance <= range) {
            fireNow = true;
          }
        }
      }
      this.touchControls.setAutoFireActive(fireNow);
    }

    if (!this.docked) {
      this.hud.updateDockPrompt(playerShip, this.state.getEntities().values());
      this.hud.updateWarpGatePrompt(
        playerShip,
        this.state.getEntities().values(),
        WARP_GATE_DISTANCE,
      );
      this.hud.updateMerchantPrompt(playerShip, this.state.getEntities().values());
      this.hud.updateLandPrompt(
        playerShip,
        this.state.getEntities().values(),
        SURFACE_LAND_DISTANCE,
      );
      this.hud.updateRuinPrompt(playerShip, this.state.getEntities().values());
      this.tickRuinInvestigate(dt);
    }

    // Re-anchor the surface sky dome to the camera every frame so the
    // sky still looks centred when the player flies to the edge of the
    // playable square.
    if (this.surfaceRenderer.isActive()) {
      const camPos = this.camera.getCamera().position;
      this.surfaceRenderer.update(camPos);
    }

    if (this.touchControls) {
      // While any UI modal is up (pause / docked station / warp UI / map /
      // inventory / leaderboard / tutorial / merchant trade window /
      // controls help / mission journal / friends panel), collapse the
      // gameplay-only controls so the joysticks and MAP / INV pills don't
      // sit on top of the modal. The MENU button stays reachable so the
      // pilot can always bail out. `input.enabled` is intentionally NOT
      // part of this check: chat-input focus disables input but we still
      // want the HUD controls visible while the pilot types.
      const gameplayActive =
        !this.docked &&
        !this.paused &&
        !this.warpGateUI.isVisible() &&
        !this.universeMap.isVisible() &&
        !this.inventoryPanel.visible &&
        !this.leaderboard.isVisible() &&
        !this.tutorial.isActive() &&
        !this.activeMerchantId &&
        !this.controls.isVisible() &&
        !this.missionJournal.isVisible() &&
        !this.friendsPanel.visible &&
        !this.chatWindow.visible;

      // Mirror HUD proximity prompts onto the touch overlay so the player
      // can tap a dedicated button instead of hunting for the on-screen
      // hint. The DOCK / WARP / TRADE pills follow the same gameplay-
      // active gate as the joysticks: a centered DOCK pill on top of the
      // OPTIONS / MAP / INVENTORY modal is just visual noise (and acts
      // as a duplicate UNDOCK while docked, since the station UI already
      // has its own corner ✕). Only surface them when the player is
      // actually flying.
      if (gameplayActive) {
        this.touchControls.setDockLabel(t('touch.dock'));
        this.touchControls.showDock(!!this.hud.nearbyStationId);
        this.touchControls.showWarp(!!this.hud.nearbyWarpGateId);
        this.touchControls.showTrade(!!this.hud.nearbyMerchantId);
        // LAND / LIFTOFF: in orbit, surface entry is gated by the
        // HUD's nearbyPlanetId (closest landable planet inside the
        // SURFACE_LAND_DISTANCE atmosphere band); on a surface, the
        // pilot has to be standing inside a Spaceport's liftoff
        // radius (HUD.nearSpaceport). Same gating rules the desktop
        // `P` key uses — the touch overlay is just a tap-friendly
        // exposure of the same trigger.
        if (this.onSurface) {
          this.touchControls.setLandLabel(t('touch.liftoff'));
          this.touchControls.showLand(this.hud.nearSpaceport);
        } else {
          this.touchControls.setLandLabel(t('touch.land'));
          this.touchControls.showLand(!!this.hud.nearbyPlanetId);
        }
      } else {
        this.touchControls.showDock(false);
        this.touchControls.showWarp(false);
        this.touchControls.showTrade(false);
        this.touchControls.showLand(false);
      }
      this.touchControls.setGameplayButtonsVisible(gameplayActive);
    }

    if (this.activeMerchantId) {
      const merchant = this.state.getEntity(this.activeMerchantId);
      if (!merchant) {
        // Merchant despawned / warped away. Drop the trade window.
        this.closeMerchantModal();
      } else if (playerShip) {
        const pp = playerShip.transform.position;
        const mp = merchant.transform.position;
        const dx = pp.x - mp.x, dy = pp.y - mp.y, dz = pp.z - mp.z;
        const d2 = dx * dx + dy * dy + dz * dz;
        // Slightly larger radius than the server's trade range so we don't
        // flicker the modal shut when the merchant drifts on its patrol.
        if (d2 > 360 * 360) this.closeMerchantModal();
      }
    }

    this.updateDebug();
    mark('debug');

    this.renderer.render(
      this.scene.getScene(),
      this.camera.getCamera(),
    );
    mark('render');

    // ── Freeze report ──────────────────────────────────────────────
    // Dump the section breakdown whenever the frame exceeds the
    // freeze threshold, OR when the local-player predictor stalls
    // for several consecutive frames despite forward input being
    // held (the on-screen freeze the user reports).
    const frameTotal = performance.now() - frameStart;
    const playerAfter = this.state.getPlayerEntity();
    const xAfter = playerAfter?.transform.position.x ?? 0;
    const zAfter = playerAfter?.transform.position.z ?? 0;
    const horizontalDelta = Math.hypot(xAfter - xBefore, zAfter - zBefore);
    const horizontalInput = Math.hypot(inputState.thrust.x, inputState.thrust.z);

    // Predictor stall = consecutive frames where the player held a
    // real horizontal input but the predictor barely advanced in
    // world-space. Lowered the threshold from 8 → 4 frames (≈ 67 ms)
    // so we report short stutters instead of waiting for an obvious
    // 130 ms freeze; that's what the user feels as "ship freezes for
    // a moment then snaps to expected destination". The 0.05 u
    // delta floor still leaves room for sub-pixel rounding so a
    // *truly* stationary frame (input clamped by collider, ceiling,
    // or the rim wall) doesn't false-positive every tick.
    if (onSurfaceBefore && horizontalInput > 0.1 && horizontalDelta < 0.05) {
      this._predictorStallFrames++;
    } else {
      this._predictorStallFrames = 0;
    }

    const isFreezeFrame = frameTotal > FREEZE_FRAME_MS;
    const isPredictorStall =
      onSurfaceBefore &&
      this._predictorStallFrames === 4;

    if (isFreezeFrame || isPredictorStall) {
      const entityCount = this.state.getEntities().size;
      const reconErr =
        (this.state as unknown as { _reconcileError?: { x: number; y: number; z: number } })._reconcileError;
      const reconMag = reconErr
        ? Math.hypot(reconErr.x, reconErr.y, reconErr.z)
        : 0;
      const playerVel = playerAfter?.transform.velocity;
      const speed = playerVel
        ? Math.hypot(playerVel.x, playerVel.y, playerVel.z)
        : 0;
      const slowSections = Object.entries(sectionTimes)
        .filter(([, ms]) => ms > SECTION_LOG_MS)
        .map(([n, ms]) => `${n}=${ms.toFixed(1)}ms`)
        .join(' ');
      const reason = isFreezeFrame
        ? `FRAME ${frameTotal.toFixed(1)}ms`
        : `PREDICTOR STALL (${this._predictorStallFrames} frames, input=${horizontalInput.toFixed(2)}, delta=${horizontalDelta.toFixed(3)})`;
      console.warn(
        `[freeze] ${reason} | entities=${entityCount} reconErr=${reconMag.toFixed(1)}u speed=${speed.toFixed(1)}u/s onSurface=${onSurfaceBefore} | ${slowSections}`,
      );
    }

    // ── Adaptive render quality ────────────────────────────────────
    // The browser-agent freeze report (2026-05-16) showed that on the
    // user's machine the EffectComposer chain (RenderPass →
    // UnrealBloomPass[5-mip downsample + 10 blur passes] →
    // ScanlineShader) eats 60–150 ms per frame on a surface even with
    // the player completely stationary — and that even at 0.5×
    // render scale the freezes do NOT go away. The biggest single
    // GPU cost in the chain is the bloom pass (≈11 of the 18 full-
    // screen draws), so the new degradation ladder shed bloom BEFORE
    // taking another pixel-ratio cut.
    //
    // Ladder (worst → best):
    //   Stage 0: bloom ON,  scale 1.00
    //   Stage 1: bloom OFF, scale 1.00   ← skip bloom first
    //   Stage 2: bloom OFF, scale 0.75
    //   Stage 3: bloom OFF, scale 0.50
    //
    // Re-upgrades go the other way: scale up first, then re-enable
    // bloom once we're back to 1.0 and have been stable for a while.
    // Hysteresis (down=8 slow frames, up=240 fast frames ≈ 4 s) keeps
    // us from flickering when the load oscillates around the
    // threshold.
    const renderMs = sectionTimes['render'] ?? 0;
    if (renderMs > 30) {
      this._slowRenderRun++;
      this._fastRenderRun = 0;
    } else if (renderMs < 12) {
      this._fastRenderRun++;
      this._slowRenderRun = 0;
    } else {
      this._slowRenderRun = 0;
      this._fastRenderRun = 0;
    }
    const currentScale = this.renderer.getRenderScale();
    const bloomOn = this.renderer.isBloomEnabled();
    if (this._slowRenderRun >= 8) {
      if (bloomOn) {
        this.renderer.setBloomEnabled(false);
        console.log(`[adaptive-quality] bloom OFF (slow run=${this._slowRenderRun}, last render=${renderMs.toFixed(1)}ms)`);
        this._slowRenderRun = 0;
      } else if (currentScale > 0.5) {
        const next = Math.max(0.5, currentScale - 0.25);
        this.renderer.setRenderScale(next);
        console.log(`[adaptive-quality] render scale ${currentScale.toFixed(2)} → ${next.toFixed(2)} (slow run=${this._slowRenderRun}, last render=${renderMs.toFixed(1)}ms)`);
        this._slowRenderRun = 0;
      }
    } else if (this._fastRenderRun >= 240) {
      if (currentScale < 1.0) {
        const next = Math.min(1.0, currentScale + 0.25);
        this.renderer.setRenderScale(next);
        console.log(`[adaptive-quality] render scale ${currentScale.toFixed(2)} → ${next.toFixed(2)} (fast run=${this._fastRenderRun})`);
        this._fastRenderRun = 0;
      } else if (!bloomOn) {
        this.renderer.setBloomEnabled(true);
        console.log(`[adaptive-quality] bloom ON (fast run=${this._fastRenderRun})`);
        this._fastRenderRun = 0;
      }
    }
  };

  private _predictorStallFrames = 0;
  private _pendingCompileRaf: number | null = null;
  /**
   * Counters that drive {@link GameRenderer.setRenderScale} from the
   * adaptive quality block at the bottom of the loop. We track runs
   * (consecutive frames) of slow / fast renders rather than rolling
   * averages because the user's pain point is *streaks* of freezes
   * — a single 50 ms blip is forgivable, eight in a row is not.
   */
  private _slowRenderRun = 0;
  private _fastRenderRun = 0;

  private createDebugPanel(container: HTMLElement): void {
    this.debugEl = document.createElement('div');
    Object.assign(this.debugEl.style, {
      position: 'absolute',
      bottom: '10px',
      left: '10px',
      background: 'rgba(0,0,0,0.7)',
      color: '#00ff41',
      fontFamily: "'Share Tech Mono', monospace",
      fontSize: '10px',
      padding: '8px 12px',
      zIndex: '500',
      pointerEvents: 'none',
      lineHeight: '1.6',
      letterSpacing: '1px',
      display: 'none',
      whiteSpace: 'pre',
      border: '1px solid rgba(0,255,65,0.2)',
    });
    container.appendChild(this.debugEl);
  }

  private updateDebug(): void {
    if (!this.debugEl || !this.debugVisible) return;
    const player = this.state.getPlayerEntity();
    const inputState = this.input.peekState();
    const entities = this.state.getEntities();

    const pos = player?.transform.position ?? { x: 0, y: 0, z: 0 };
    const vel = player?.transform.velocity ?? { x: 0, y: 0, z: 0 };
    const rot = player?.transform.rotation ?? { x: 0, y: 0, z: 0 };
    const speed = Math.sqrt(vel.x * vel.x + vel.y * vel.y + vel.z * vel.z);

    const srv = this.state.getLastServerTransform();
    const reconDist = this.state.getLastReconcileDistance();

    const f = (v: number, d = 1) => v.toFixed(d);
    const v3 = (v: { x: number; y: number; z: number }, d = 1) =>
      `${f(v.x, d)}, ${f(v.y, d)}, ${f(v.z, d)}`;

    let text =
      `── CLIENT ──────────────────\n` +
      `POS  ${v3(pos)}\n` +
      `VEL  ${f(speed)} (${v3(vel)})\n` +
      `ROT  ${v3(rot, 3)} (deg: ${f(rot.x * 180 / Math.PI)}, ${f(rot.y * 180 / Math.PI)})\n`;

    if (srv) {
      const sSpeed = Math.sqrt(srv.velocity.x ** 2 + srv.velocity.y ** 2 + srv.velocity.z ** 2);
      text +=
        `── SERVER ──────────────────\n` +
        `POS  ${v3(srv.position)}\n` +
        `VEL  ${f(sSpeed)} (${v3(srv.velocity)})\n` +
        `ROT  ${v3(srv.rotation, 3)}\n` +
        `DRIFT ${f(reconDist, 2)} units\n`;
    }

    if (inputState) {
      text +=
        `── INPUT ───────────────────\n` +
        `THR  ${v3(inputState.thrust, 2)}\n` +
        `MROT ${v3(inputState.rotation, 4)}\n` +
        `FIRE ${inputState.isFiring ? 'YES' : 'no'}  BOOST ${inputState.isBoosting ? 'YES' : 'no'}\n` +
        `LOCK ${inputState.pointerLocked ? 'YES' : 'no'}  SLOT ${inputState.weaponSlot}\n`;
    }

    text +=
      `── NET ─────────────────────\n` +
      `PING ${f(this.network.latency)}ms  ENT ${entities.size}\n` +
      `CONN ${this.network.isConnected() ? 'ONLINE' : 'OFFLINE'}\n` +
      `PLR  ${player ? player.id.slice(0, 8) : 'NONE'}`;

    this.debugEl.textContent = text;
  }

  /**
   * Per-DEVICE audio preference. Intentional exception to the
   * "no localStorage-only state" rule: this controls whether the
   * background music plays on THIS browser/install, which by player
   * convention is per-device (mute on phone, on at desktop). It is
   * not gameplay state, not visible to other players, and never
   * influences server decisions, so syncing it through the server
   * would add cross-device coupling without observable benefit.
   *
   * If we ever want a "sync UI prefs across devices" feature, this
   * key should move into a server-pushed preferences blob alongside
   * any other per-account prefs we add — at which point delete this
   * exception comment.
   */
  private loadMusicEnabled(): boolean {
    try {
      const raw = localStorage.getItem('vs_music_enabled');
      if (raw === null) return true;
      return raw === '1';
    } catch {
      return true;
    }
  }

  private saveMusicEnabled(enabled: boolean): void {
    try {
      localStorage.setItem('vs_music_enabled', enabled ? '1' : '0');
    } catch {
      /* storage blocked (private mode / quota) — preference is session-only */
    }
  }

  /**
   * Persisted-credentials key. Stores `{ username, password }` for the
   * last successful real-account login (guest sessions never write here)
   * so the next app launch can auto-login without prompting.
   *
   * Intentional exception to the "no localStorage-only state" rule:
   * this is per-DEVICE auto-sign-in convenience that mirrors a browser
   * password manager. The plaintext password MUST NOT be sent to the
   * server for storage — every server-side store of a user-supplied
   * password is a recipe for a credential dump. Same threat surface as
   * any "remember me" flow in a native app: anyone with physical or
   * DevTools access to this device can read it. The token gets cleared
   * on sign-out and on any auth failure so a stale wrong password
   * never loops forever.
   */
  private static readonly AUTH_CREDS_KEY = 'vs_auth_creds_v1';

  private loadSavedCredentials(): { username: string; password: string } | null {
    try {
      const raw = localStorage.getItem(ViralSpace.AUTH_CREDS_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Record<string, unknown> | null;
      if (
        parsed &&
        typeof parsed.username === 'string' &&
        parsed.username.length > 0 &&
        typeof parsed.password === 'string' &&
        parsed.password.length > 0
      ) {
        return { username: parsed.username, password: parsed.password };
      }
      return null;
    } catch {
      return null;
    }
  }

  private saveCredentials(username: string, password: string): void {
    try {
      localStorage.setItem(
        ViralSpace.AUTH_CREDS_KEY,
        JSON.stringify({ username, password }),
      );
    } catch {
      /* storage blocked (private mode / quota) — auto-login simply won't kick in next launch */
    }
  }

  private clearSavedCredentials(): void {
    try {
      localStorage.removeItem(ViralSpace.AUTH_CREDS_KEY);
    } catch {
      /* swallow */
    }
  }

  private syncMapSectorClaims(): void {
    this.syncMapWorldState();
  }

  /**
   * Shove the latest claims + active sector events into both map UIs
   * if either is currently open. Centralized here so message handlers
   * (`SectorClaimUpdate`, `SectorEventUpdate`, …) don't have to know
   * which map happens to be visible.
   */
  private syncMapWorldState(): void {
    const claims = this.state.getAllSectorClaims();
    const activeEvents = this.state.getAllActiveSectorEvents();
    if (this.warpGateUI.isVisible()) {
      this.warpGateUI.updatePlayerState({
        discovered: this.state.getDiscoveredSectors(),
        credits: this.state.getPlayerState().credits,
        current: this.state.getPlayerState().sector,
        claims,
        activeEvents,
      });
    }
    if (this.universeMap.isVisible()) {
      this.universeMap.updatePlayerState({
        current: this.state.getPlayerState().sector,
        discovered: this.state.getDiscoveredSectors(),
        destination: this.state.getNavigationTarget(),
        claims,
        activeEvents,
      });
    }
  }

  private handleSiegePhaseTransition(
    msg: SiegeUpdateMsg,
    prev: string | undefined,
  ): void {
    if (msg.phase === prev) return;
    if (msg.phase === 'underSiege' && prev !== 'underSiege') {
      this.audio.playUI('notification');
      this.chat.addSystemFromServer(
        t('siege.system.underSiege', { sector: msg.sectorKey }),
      );
      return;
    }
    if (msg.phase === 'interrupted') {
      this.audio.playUI('notification');
      this.chat.addSystemFromServer(
        t('siege.system.interrupted', { sector: msg.sectorKey }),
      );
      return;
    }
    if (msg.phase === 'transferred') {
      this.audio.playUI('notification');
      this.chat.addSystemFromServer(
        t('siege.system.transferred', {
          sector: msg.sectorKey,
          attacker: msg.attackerName ?? '?',
        }),
      );
    }
  }

  private updateSiegeBanner(): void {
    const acc = this.state.getAccountPlayerId();
    if (!acc) {
      this.hud.setSiegeBanner(null);
      return;
    }
    for (const claim of this.state.getAllSectorClaims()) {
      if (claim.ownerPlayerId === acc && claim.siege?.phase === 'underSiege') {
        const s = claim.siege;
        const ends = s.endsAt > 0 ? Math.max(0, s.endsAt - Date.now()) : 0;
        const mm = Math.floor(ends / 60_000);
        const ss = Math.floor((ends % 60_000) / 1000);
        const countdown = `${mm}:${ss.toString().padStart(2, '0')}`;
        this.hud.setSiegeBanner(t('siege.defenderBanner', { countdown }));
        return;
      }
    }
    for (const claim of this.state.getAllSectorClaims()) {
      const s = claim.siege;
      if (s?.attackerId === acc && (s.phase === 'underSiege' || s.phase === 'contested')) {
        const ends = s.endsAt > 0 ? Math.max(0, s.endsAt - Date.now()) : 0;
        const mm = Math.floor(ends / 60_000);
        const ss = Math.floor((ends % 60_000) / 1000);
        const countdown = `${mm}:${ss.toString().padStart(2, '0')}`;
        this.hud.setSiegeBanner(t('siege.attackerBanner', { countdown }));
        return;
      }
    }
    this.hud.setSiegeBanner(null);
  }

  private statusLabelForClaim(c: SectorClaim): string {
    if (!c.siege) return t('territory.status.idle');
    switch (c.siege.phase) {
      case 'contested':
        return t('territory.status.contested');
      case 'underSiege':
        return t('territory.status.underSiege');
      case 'interrupted':
        return t('territory.status.interrupted');
      default:
        return t('territory.status.idle');
    }
  }

  /**
   * Build the rich Territory tab payload for the docked sector.
   *
   * The renderer in `station-ui.ts` is purely presentational, so this
   * helper does the heavy lifting:
   *   - Resolves the sector's narrative status (frontier / lore-locked /
   *     yours / enemy / cooldown) from the deterministic dominant race
   *     plus any live `SectorClaim`.
   *   - Packages the sector race (or owner race) into a self-contained
   *     `TerritoryRaceCard` with localized name/description and the
   *     headline race traits.
   *   - Surfaces server-authoritative siege state and cooldown timers
   *     so the UI can render countdowns directly.
   *   - Inlines the constants the UI uses to explain owner perks
   *     (shop discount, revenue share, siege window) — keeping the
   *     "how it works" copy in lockstep with `game.ts`.
   */
  private buildTerritoryPanel(): TerritoryPanelData {
    const ps = this.state.getPlayerState();
    const sk = sectorKey(ps.sector);
    const coord = ps.sector;
    const dr = dominantRace(coord);
    const claim = this.state.getSectorClaim(sk);
    const acc = this.state.getAccountPlayerId();
    const tier = getZoneDifficulty(coord);
    // `CLAIM_COST_BY_DIFFICULTY` covers tiers 1..9; the fallback is the
    // last-band cost so a future tier extension never accidentally
    // hands back the cheap inner-tier claim price.
    const claimCost = CLAIM_COST_BY_DIFFICULTY[tier] ?? CLAIM_COST_BY_DIFFICULTY[9] ?? 50_000;
    const now = Date.now();

    // Status resolution. Order matters: a player's claim trumps the
    // dominant-race fallback, and a fresh transfer cooldown takes
    // precedence over "frontier" so the UI shows the right reason
    // when the action button is missing.
    let status: TerritoryStatus;
    if (claim?.ownerPlayerId && claim.ownerPlayerId === acc) {
      status = 'yours';
    } else if (claim?.ownerPlayerId) {
      status = 'enemy';
    } else if (dr !== null) {
      status = 'lore-locked';
    } else if ((claim?.siegeCooldownUntil ?? 0) > now) {
      status = 'cooldown';
    } else {
      status = 'frontier';
    }

    // Race card: prefer the owner-race when a player claim exists,
    // otherwise fall back to the deterministic dominant race. Pure
    // frontier returns `null` so the renderer can show the unclaimed
    // pitch.
    const raceForCard: number | null =
      claim?.ownerRace != null ? claim.ownerRace : dr;
    const race: TerritoryRaceCard | null = (() => {
      if (raceForCard === null) return null;
      const info = RACE_INFO[raceForCard as RaceId];
      if (!info) return null;
      // Use the i18n registry so any locale (en/es/pt and any
      // operator-loaded packs) gets the right translation, with
      // graceful fallback to English baked in by `t()`.
      const locale = getLocale();
      return {
        raceId: raceForCard,
        name: t(raceNameKey(raceForCard), locale),
        description: t(raceDescriptionKey(raceForCard), locale),
        disposition: info.disposition,
        primaryColor: info.primaryColor,
        secondaryColor: info.secondaryColor,
        traitLines: formatRaceTraitSummary(raceForCard, locale),
      };
    })();

    const owned = this.state.getAllSectorClaims().filter((c) => c.ownerPlayerId === acc);
    const ownedSectors = owned.map((c) => ({
      sectorKey: c.sectorKey,
      label: c.sectorKey,
      statusLabel: this.statusLabelForClaim(c),
      raceColor: c.ownerRace != null
        ? RACE_INFO[c.ownerRace as RaceId]?.primaryColor
        : undefined,
      garrisonLevel: c.garrisonLevel,
      isCurrent: c.sectorKey === sk,
    }));

    const canClaim = status === 'frontier';

    let canReinforce = false;
    let reinforceCost: number | null = null;
    let targetGarrisonLevel: number | null = null;
    if (status === 'yours' && (claim?.garrisonLevel ?? 0) < GARRISON_LEVEL_MAX) {
      canReinforce = true;
      targetGarrisonLevel = (claim?.garrisonLevel ?? 0) + 1;
      reinforceCost = GARRISON_REINFORCE_COST_BY_LEVEL[targetGarrisonLevel] ?? 0;
    }

    let hint: string | undefined;
    if (status === 'lore-locked') {
      hint = t('claim.notClaimable');
    } else if (status === 'enemy') {
      hint = t('claim.alreadyOwned');
    } else if (status === 'cooldown') {
      hint = t('claim.onCooldown');
    } else if (status === 'yours' && !canReinforce) {
      hint = t('claim.maxGarrison');
    }

    // Siege state — a fresh siege event from the server populates
    // `claim.siege`; we surface phase + countdown so the renderer can
    // animate the "your sector is under attack" banner without needing
    // to repeatedly poll the network layer.
    const phase = claim?.siege?.phase;
    const surfacedPhase: 'contested' | 'underSiege' | 'interrupted' | null =
      phase === 'contested' || phase === 'underSiege' || phase === 'interrupted'
        ? phase
        : null;

    return {
      sectorKey: sk,
      sectorName: sectorName(coord),
      difficultyTier: tier,
      status,
      race,
      ownerName: claim?.ownerName ?? null,
      ownerIsYou: status === 'yours',
      garrisonLevel: claim?.garrisonLevel ?? 0,
      garrisonLevelMax: GARRISON_LEVEL_MAX,
      garrisonNpcsPerLevel: GARRISON_NPCS_PER_LEVEL,
      siegePhase: surfacedPhase,
      siegeEndsAt: claim?.siege?.endsAt ?? null,
      siegeAttackerName: claim?.siege?.attackerName ?? null,
      cooldownUntil: claim?.siegeCooldownUntil && claim.siegeCooldownUntil > now
        ? claim.siegeCooldownUntil
        : null,
      siegeWindowMinutes: Math.round(SIEGE_WINDOW_MS / 60_000),
      ownerShopDiscountPct: Math.round(OWNER_SHOP_DISCOUNT * 100),
      ownerRevenueSharePct: Math.round(OWNER_REVENUE_SHARE * 100),
      ownerRevenueDailyCap: OWNER_REVENUE_DAILY_CAP,
      claimCost,
      reinforceCost,
      targetGarrisonLevel: canReinforce ? targetGarrisonLevel : null,
      canClaim,
      canReinforce,
      hint,
      ownedSectors,
    };
  }

  /**
   * Drive the per-frame surface-ruin investigate timer. Resets to 0
   * whenever:
   *   - the pilot leaves the ruin's radius (HUD nearbyRuinId flips)
   *   - the pilot releases KeyE
   *   - the pilot drifts to a different ruin (new site id)
   * On reaching {@link RUIN_INVESTIGATE_DURATION_MS} the client fires
   * a single {@link InvestigateRuin} message to the server and locks
   * the timer until the next key-down (so the player can't double-tap
   * for a second unlock).
   */
  private tickRuinInvestigate(dt: number): void {
    const siteId = this.hud.nearbyRuinId;
    if (!siteId) {
      if (this._ruinInvestigateMs !== 0) {
        this._ruinInvestigateMs = 0;
        this._ruinInvestigateSiteId = null;
        this.hud.setRuinProgress(0);
      }
      return;
    }
    if (this._ruinInvestigateSiteId !== siteId) {
      this._ruinInvestigateSiteId = siteId;
      this._ruinInvestigateMs = 0;
    }
    if (!this._keyEHeld) {
      if (this._ruinInvestigateMs !== 0) {
        this._ruinInvestigateMs = 0;
        this.hud.setRuinProgress(0);
      }
      return;
    }
    this._ruinInvestigateMs = Math.min(
      ViralSpace.RUIN_INVESTIGATE_DURATION_MS_CONST,
      this._ruinInvestigateMs + dt * 1000,
    );
    this.hud.setRuinProgress(
      this._ruinInvestigateMs / ViralSpace.RUIN_INVESTIGATE_DURATION_MS_CONST,
    );
    if (this._ruinInvestigateMs >= ViralSpace.RUIN_INVESTIGATE_DURATION_MS_CONST) {
      // Fire once + lock until the player either releases KeyE
      // (which resets the timer above) or moves out of range.
      this.network.sendInvestigateRuin(siteId);
      this._ruinInvestigateMs = 0;
      this.hud.setRuinProgress(0);
      // Force a tiny cooldown by clearing the held flag so a
      // continuously-held KeyE doesn't immediately re-fire.
      this._keyEHeld = false;
      this.audio.playUI('confirm');
    }
  }

  private dockAtStation(stationId: string): void {
    this.network.sendDockRequest(stationId);
    this.docked = true;
    this.dockStationId = stationId;
    this.dockSectorKey = sectorKey(this.state.getPlayerState().sector);
    this.input.disable();
    this.hud.hide();
    this.worldLabels.dispose();
    this.audio.playUI('confirm');

    const stationEntity = this.state.getEntity(stationId);
    const stationName = stationEntity?.name ?? 'SPACE STATION';
    const extra = stationEntity?.extra ?? {};
    const or = extra['ownerRace'] as number | undefined;
    const factionName =
      or !== undefined && or !== null && RACE_INFO[or as keyof typeof RACE_INFO]
        ? RACE_INFO[or as keyof typeof RACE_INFO].name
        : 'NEUTRAL';

    const playerLevel = this.state.getPlayerState().level ?? 1;
    const resources = this.buildPlayerResourceSnapshot().resources;

    const weaponLabel = t('station.catWeapon');
    const shieldLabel = t('station.catShield');
    const engineLabel = t('station.catEngine');
    const consumableLabel = t('station.catConsumable');

    const shopItems: StationData['shopItems'] = [];

    const buildIngredients = (
      cost: { resource: ResourceTier; amount: number }[],
    ) => cost.map((c) => ({
      name: RESOURCE_INFO[c.resource]?.name ?? String(c.resource),
      amount: c.amount,
      have: resources.get(c.resource) ?? 0,
    }));

    // Intentionally loose typing for the merged crafting list — each family
    // (weapons/shields/consumables) has a different definition shape and
    // the only fields we touch here are the common subset (id, name,
    // description, requiredLevel, craftCost). Narrowing with `as any` keeps
    // the call site readable without inventing a shared super-type.
    type CraftDef = {
      id: string;
      name: string;
      nameEs: string;
      description?: string;
      descriptionEs?: string;
      requiredLevel: number;
      craftCost?: { resource: ResourceTier; amount: number }[];
    };
    const craftables: Array<{ def: CraftDef; categoryKey: 'weapon' | 'shield' | 'consumable'; label: string }> = [
      ...WEAPONS_ORDERED
        .filter((w) => w.craftCost && w.craftCost.length > 0)
        .map((w) => ({ def: w as CraftDef, categoryKey: 'weapon' as const, label: weaponLabel })),
      ...SHIELDS_ORDERED
        .filter((s) => s.craftCost && s.craftCost.length > 0)
        .map((s) => ({ def: s as CraftDef, categoryKey: 'shield' as const, label: shieldLabel })),
      ...CONSUMABLES_ORDERED
        .filter((c) => c.craftCost && c.craftCost.length > 0)
        .map((c) => ({ def: c as CraftDef, categoryKey: 'consumable' as const, label: consumableLabel })),
    ];
    const recipes = craftables.map(({ def, categoryKey, label }) => {
      const ingredients = buildIngredients(def.craftCost ?? []);
      const hasIngredients = ingredients.every((i) => i.have >= i.amount);
      const levelOk = playerLevel >= def.requiredLevel;
      const canCraft = hasIngredients && levelOk;
      return {
        id: def.id,
        name: itemLabel(categoryKey, def.id, def.name),
        description: itemDescription(categoryKey, def.id, def.description ?? ''),
        ingredients,
        craftTime: 0,
        canCraft,
        requiredLevel: def.requiredLevel,
        category: label,
        categoryKey,
        reason: !levelOk
          ? `LVL ${def.requiredLevel}+`
          : !hasIngredients
            ? t('station.missingMat')
            : undefined,
      };
    });

    const missionRows = this.state.getStationMissions();
    const activeIds = new Set(this.state.getActiveMissions().map((m) => m.id));
    const missions = missionRows.map((m) => ({
      id: m.id,
      title: m.title,
      description: m.description,
      rewardXp: m.rewards.xp,
      rewardCredits: m.rewards.credits,
      requiredLevel: m.requiredLevel,
      isActive: activeIds.has(m.id),
      premiumOnly: m.premiumOnly === true,
    }));

    const data: StationData = {
      name: stationName,
      faction: factionName,
      shopItems,
      recipes,
      missions,
      territory: this.buildTerritoryPanel(),
      hangar: {
        owned: this.state.getPlayerState().ownedShipClasses.slice(),
        active: this.state.getPlayerState().shipClass ?? 0,
        credits: this.state.getPlayerState().credits ?? 0,
        level: this.state.getPlayerState().level ?? 1,
        raceId: this.state.getPlayerState().raceId,
        cosmetics: this.shipCosmetics,
      },
      // Phase 3.2 — when docked at a surface base, expose the
      // surface id (which equals the dock sector key for surface
      // sectors) so the dock UI can render the reputation meter
      // and the Liberator badge.
      ...(this.dockSectorKey && this.dockSectorKey.startsWith('surface:')
        ? { surfaceId: this.dockSectorKey }
        : {}),
    };

    this.stationUI.show(data);
  }

  private handleBuyShip(shipClass: number): void {
    if (!this.docked) return;
    this.network.sendBuyShip(shipClass);
    this.audio.playUI('confirm');
  }

  private handleSwitchShip(shipClass: number): void {
    if (!this.docked) return;
    this.network.sendSwitchShip(shipClass);
    this.audio.playUI('confirm');
  }

  private undockFromStation(): void {
    this.docked = false;
    this.dockSectorKey = null;
    this.dockStationId = null;
    this.pendingBuy = null;
    this.pendingCraft = null;
    this.state.clearStationMissions();
    this.stationUI.hide();
    this.network.sendUndockRequest();
    this.hud.show();
    this.input.enable();
  }

  private hailMerchant(merchantId: string): void {
    this.activeMerchantId = merchantId;
    this.network.sendMerchantHail(merchantId);
    this.audio.playUI('confirm');
  }

  /**
   * Render the server-provided merchant stock in a lightweight floating
   * panel. Unlike station docking, the player's ship stays in flight — the
   * modal closes automatically if the player warps or the merchant moves
   * out of range. Buy requests reuse the standard BuyItem message tagged
   * with `merchantId` so the server can enforce range + stock gating.
   */
  private showMerchantModal(offer: MerchantOfferMsg): void {
    this.closeMerchantModal();
    this.activeMerchantId = offer.merchantId;

    // Resolve an item's `ItemKind` so we can look up its localized
    // name/description from the shared i18n registry. Returns null for
    // unknown ids (handled by the caller falling back to raw text).
    const kindOf = (id: string): 'weapon' | 'shield' | 'engine' | 'consumable' | null => {
      if (WEAPONS[id]) return 'weapon';
      if (SHIELDS[id]) return 'shield';
      if (ENGINES[id]) return 'engine';
      if (CONSUMABLES[id]) return 'consumable';
      return null;
    };

    const overlay = document.createElement('div');
    overlay.style.cssText = [
      'position:fixed', 'inset:0',
      'background:rgba(0,0,0,0.65)',
      'display:flex', 'align-items:center', 'justify-content:center',
      'z-index:9000',
      'font-family:"Rajdhani","Segoe UI",sans-serif',
    ].join(';');

    const tierColor = offer.tier === 'legendary' ? '#ff88ff'
      : offer.tier === 'rare' ? '#ffcc44' : '#00ffa0';

    const modal = document.createElement('div');
    modal.style.cssText = [
      'min-width:540px', 'max-width:760px', 'max-height:78vh',
      'background:#050912', `border:1px solid ${tierColor}`,
      `box-shadow:0 0 24px ${tierColor}55`,
      'color:#e0f0ff', 'padding:18px 22px 14px',
      'display:flex', 'flex-direction:column', 'gap:10px',
      'border-radius:4px',
    ].join(';');

    const title = document.createElement('div');
    title.innerHTML = `
      <div style="font-size:11px;letter-spacing:3px;color:${tierColor};opacity:0.7;">
        ${t('merchant.title')} · ${offer.tier.toUpperCase()}
      </div>
      <div style="font-size:22px;letter-spacing:2px;color:${tierColor};text-shadow:0 0 8px ${tierColor}66;">
        ${offer.merchantName.replace(/_/g, ' ').toUpperCase()}
      </div>
      ${offer.greeting ? `<div style="font-size:13px;opacity:0.8;font-style:italic;margin-top:4px;">"${offer.greeting}"</div>` : ''}
    `;
    modal.appendChild(title);

    const credits = this.state.getPlayerState().credits ?? 0;
    const playerLevel = this.state.getPlayerState().level ?? 1;
    const eqSnap = this.state.getEquipment();
    const ownedEngineIds = new Set<string>(
      (eqSnap?.ownedEngines ?? []).map((e) => e.id),
    );
    const creditsRow = document.createElement('div');
    creditsRow.style.cssText = 'display:flex;justify-content:space-between;font-size:12px;color:#aaddff;letter-spacing:1px;padding:4px 0;border-top:1px solid #1a2a3a;border-bottom:1px solid #1a2a3a;';
    creditsRow.innerHTML = `<span>${t('merchant.credits')}: <b style="color:#ffff88;">${credits.toLocaleString()}</b></span><span>${t('merchant.level')} ${playerLevel}</span>`;
    modal.appendChild(creditsRow);

    const list = document.createElement('div');
    list.style.cssText = 'overflow-y:auto;max-height:50vh;display:flex;flex-direction:column;gap:6px;padding:6px 0;';

    const lookupDef = (id: string) => WEAPONS[id] ?? SHIELDS[id] ?? ENGINES[id] ?? CONSUMABLES[id];

    for (const entry of offer.shopItems) {
      const def = lookupDef(entry.itemId);
      if (!def) continue;
      const row = document.createElement('div');
      const rarityTint = entry.rarity === 'legendary' ? '#ff88ff'
        : entry.rarity === 'rare' ? '#ffcc44' : '#6fc9ff';
      const affordable = credits >= entry.price;
      const levelOk = playerLevel >= def.requiredLevel;
      const isEngine = !!ENGINES[entry.itemId];
      const alreadyOwned = isEngine && ownedEngineIds.has(entry.itemId);
      const canBuy = affordable && levelOk && !alreadyOwned;
      row.style.cssText = [
        'display:grid',
        'grid-template-columns:1fr auto auto',
        'gap:12px',
        'align-items:center',
        'padding:8px 10px',
        `border:1px solid ${rarityTint}44`,
        'background:#0a121e',
      ].join(';');

      const kind = kindOf(entry.itemId);
      const localizedName = kind
        ? itemLabel(kind, entry.itemId, def.name)
        : def.name;
      const localizedDesc = kind
        ? itemDescription(kind, entry.itemId, (def as { description?: string }).description ?? '')
        : ((def as { description?: string }).description ?? '');

      const info = document.createElement('div');
      info.innerHTML = `
        <div style="font-size:14px;color:${rarityTint};letter-spacing:1px;">${localizedName}</div>
        <div style="font-size:11px;opacity:0.65;line-height:1.3;margin-top:2px;">${localizedDesc}</div>
        <div style="font-size:10px;margin-top:3px;opacity:0.5;letter-spacing:1px;">LVL ${def.requiredLevel} · ${entry.rarity.toUpperCase()}</div>
      `;

      const price = document.createElement('div');
      price.style.cssText = `font-size:14px;color:${affordable ? '#ffff88' : '#ff6666'};font-weight:bold;`;
      price.textContent = `${entry.price.toLocaleString()} ¢`;

      const buy = document.createElement('button');
      buy.textContent = t('merchant.buy');
      buy.disabled = !canBuy;
      buy.style.cssText = [
        'padding:6px 12px',
        `background:${canBuy ? tierColor : '#222'}`,
        `color:${canBuy ? '#001018' : '#666'}`,
        'border:none', 'font-family:inherit',
        'letter-spacing:2px', 'font-weight:bold',
        `cursor:${canBuy ? 'pointer' : 'not-allowed'}`,
      ].join(';');
      buy.addEventListener('click', () => {
        if (!canBuy) return;
        this.network.sendBuyItem(entry.itemId, 1, offer.merchantId);
        this.audio.playUI('select');
      });
      if (!levelOk) buy.textContent = `LVL ${def.requiredLevel}+`;
      else if (alreadyOwned) buy.textContent = t('station.buyOwned');
      row.append(info, price, buy);
      list.appendChild(row);
    }

    if (offer.shopItems.length === 0) {
      const empty = document.createElement('div');
      empty.style.cssText = 'padding:18px;text-align:center;opacity:0.6;font-style:italic;';
      empty.textContent = t('merchant.empty');
      list.appendChild(empty);
    }

    modal.appendChild(list);

    const close = document.createElement('button');
    close.textContent = t('merchant.close');
    close.style.cssText = [
      'align-self:flex-end',
      'padding:6px 16px', 'margin-top:4px',
      'background:#10202a', `color:${tierColor}`,
      `border:1px solid ${tierColor}66`,
      'font-family:inherit', 'letter-spacing:2px',
      'cursor:pointer',
    ].join(';');
    close.addEventListener('click', () => this.closeMerchantModal());
    modal.appendChild(close);

    overlay.appendChild(modal);
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) this.closeMerchantModal();
    });
    document.body.appendChild(overlay);
    this.merchantModalEl = overlay;

    // Refresh the modal any time inventory / credits change so BUY buttons
    // re-evaluate affordability. Cheap rebuild beats re-wiring every row.
    const unsub = this.network.on(MessageType.InventoryUpdate, () => {
      if (this.activeMerchantId === offer.merchantId) {
        this.showMerchantModal(offer);
        unsub();
      } else {
        unsub();
      }
    });
  }

  private closeMerchantModal(): void {
    if (this.merchantModalEl) {
      this.merchantModalEl.remove();
      this.merchantModalEl = null;
    }
    if (this.activeMerchantId) {
      this.network.sendMerchantClose(this.activeMerchantId);
      this.activeMerchantId = null;
    }
  }

  private buildPlayerResourceSnapshot() {
    const ps = this.state.getPlayerState();
    const resources = new Map<ResourceTier, number>();
    for (const item of ps.inventory) {
      const match = item.itemId.match(/^resource_(\d+)$/);
      if (match) {
        const tier = parseInt(match[1], 10) as ResourceTier;
        resources.set(tier, (resources.get(tier) ?? 0) + item.quantity);
      }
    }
    return { resources, credits: ps.credits };
  }

  /**
   * Resolve an opaque inventory `itemId` (e.g. `resource_3`,
   * `consumable_repair_kit`, `terran_mass_driver`) into a localized
   * display label for HUD toasts. Falls back to the raw id when no
   * lookup matches so we never show "undefined".
   */
  private friendlyItemLabel(itemId: string): string {
    const resourceMatch = itemId.match(/^resource_(\d+)$/);
    if (resourceMatch) {
      const tier = parseInt(resourceMatch[1], 10) as ResourceTier;
      const info = RESOURCE_INFO[tier];
      if (info) return info.name;
    }
    const def = CONSUMABLES[itemId as keyof typeof CONSUMABLES];
    if (def) return itemLabel('consumable', itemId, def.name);
    const w = WEAPONS[itemId as keyof typeof WEAPONS];
    if (w) return itemLabel('weapon', itemId, w.name);
    const s = SHIELDS[itemId as keyof typeof SHIELDS];
    if (s) return itemLabel('shield', itemId, s.name);
    const e = ENGINES[itemId as keyof typeof ENGINES];
    if (e) return itemLabel('engine', itemId, e.name);
    return itemId;
  }

  private openWarpGateUI(gateId: string): void {
    this.pendingWarpGateId = gateId;
    const ps = this.state.getPlayerState();
    const discovered = this.state.getDiscoveredSectors();
    if (!discovered.some((s) => sectorKey(s) === sectorKey(ps.sector))) {
      discovered.push({ ...ps.sector });
    }
    this.input.disable();
    this.warpGateUI.setPremium(this.premium.active);
    this.warpGateUI.show({
      current: { ...ps.sector },
      discovered,
      credits: ps.credits,
      claims: this.state.getAllSectorClaims(),
      activeEvents: this.state.getAllActiveSectorEvents(),
    });
    this.audio.playUI('confirm');
  }

  private closeWarpGateUI(): void {
    this.pendingWarpGateId = null;
    if (this.warpGateUI.isVisible()) {
      this.warpGateUI.hide();
    }
    if (this.running && !this.paused && !this.docked) {
      this.input.enable();
    }
  }

  private openUniverseMap(): void {
    const ps = this.state.getPlayerState();
    this.input.disable();
    this.universeMap.show({
      current: { ...ps.sector },
      discovered: this.state.getDiscoveredSectors(),
      destination: this.state.getNavigationTarget(),
      claims: this.state.getAllSectorClaims(),
      activeEvents: this.state.getAllActiveSectorEvents(),
    });
    this.audio.playUI('select');
  }

  private closeUniverseMap(): void {
    if (this.universeMap.isVisible()) {
      this.universeMap.hide();
    }
    if (this.running && !this.paused && !this.docked && !this.inventoryPanel.visible) {
      this.input.enable();
    }
  }

  private openMissionJournal(): void {
    if (this.missionJournal.isVisible()) return;
    this.missionJournal.show();
    this.input.disable();
    this.audio.playUI('select');
  }

  private closeMissionJournal(): void {
    if (this.missionJournal.isVisible()) {
      this.missionJournal.hide();
    }
    if (this.running && !this.paused && !this.docked) {
      this.input.enable();
    }
  }

  private toggleMissionJournal(): void {
    if (this.missionJournal.isVisible()) {
      this.closeMissionJournal();
    } else {
      this.openMissionJournal();
    }
  }

  private openLeaderboard(): void {
    if (this.leaderboard.isVisible()) return;
    this.input.disable();
    this.leaderboard.show();
    this.audio.playUI('select');
  }

  private closeLeaderboard(): void {
    if (this.leaderboard.isVisible()) {
      this.leaderboard.hide();
    }
    if (
      this.running &&
      !this.paused &&
      !this.docked &&
      !this.inventoryPanel.visible &&
      !this.warpGateUI.isVisible() &&
      !this.universeMap.isVisible()
    ) {
      this.input.enable();
    }
  }

  /** Counters for the occasional free-pilot "Premium earns 1.5x XP" hint. */
  private xpGainsSinceHint = 0;
  private lastXpHintAt = 0;

  /**
   * Occasional, deliberately low-pressure upsell: every ~60 XP toasts
   * (and at most once per 10 minutes) free pilots get a one-line
   * reminder that Premium accounts level 1.5x faster. Frequency tuned
   * to stay informative rather than nagging.
   */
  private maybeShowPremiumXpHint(): void {
    this.xpGainsSinceHint++;
    const now = Date.now();
    if (this.xpGainsSinceHint < 60 || now - this.lastXpHintAt < 600_000) return;
    this.xpGainsSinceHint = 0;
    this.lastXpHintAt = now;
    this.hud.showNotification(t('xp.premiumHint'), 0xffd700);
  }

  /**
   * Land action router. Premium-locked planets short-circuit into the
   * upgrade modal (the server would reject the request anyway with
   * `planet_premium_only`); everything else sends the normal request.
   */
  private tryLandOnPlanet(planetId: string): void {
    if (this.hud.nearbyPlanetPremiumLocked) {
      this.hud.showNotification(t('planet.premiumOnly'), 0xffd700);
      this.openUpgradeModal('planet');
      return;
    }
    this.network.sendLandRequest(planetId);
  }

  private requestWarpTravel(destination: SectorCoord): void {
    const gateId = this.pendingWarpGateId ?? this.hud.nearbyWarpGateId;
    if (!gateId) {
      this.warpGateUI.setError(this.translateWarpError('not_near_warp_gate'));
      return;
    }
    this.network.sendWarpGateUse(gateId, destination);
  }

  private translateWarpError(error?: string): string {
    switch (error) {
      case 'insufficient_credits':
        return 'INSUFFICIENT CREDITS';
      case 'destination_not_discovered':
        return 'SECTOR NOT DISCOVERED';
      case 'not_near_warp_gate':
        return 'OUT OF RANGE OF WARP GATE';
      case 'already_in_sector':
        return 'ALREADY IN SECTOR';
      case 'destination_has_no_gate':
      case 'destination_unavailable':
        return 'NO GATE AT DESTINATION';
      case 'warp_range_premium':
        return t('warpGate.premiumRangeShort');
      default:
        return 'WARP FAILED';
    }
  }

  dispose() {
    this.running = false;
    this.network.disconnect();
    this.input.dispose();
    this.renderer.dispose();
    this.audio.dispose();
    this.controls.dispose();
    this.tutorial.dispose();
    this.pauseMenu.dispose();
    this.upgradeModal?.dispose();
    this.upgradeModal = null;
    this.worldLabels.dispose();
    this.effects.dispose();
    this.inventoryPanel.dispose();
    this.warpGateUI.dispose();
    this.universeMap.dispose();
    this.leaderboard.dispose();
    this.touchControls?.dispose();
    this.mobileOverlay?.dispose();
  }
}

/**
 * Resolve the WebSocket URL of the game server.
 *
 * Order of precedence:
 *   1. `VITE_SERVER_URL_ANDROID` when running inside the Capacitor APK /
 *      iOS shell — those WebViews load the bundle from `https://localhost`
 *      so the browser-style auto-detect below would point at a server the
 *      phone cannot reach.
 *   2. `VITE_SERVER_URL_DESKTOP` when running inside the Electron / Steam
 *      shell. The desktop bundle is served from `file://` so the same
 *      auto-detect failure mode applies — we must bake the public host
 *      into the build.
 *   3. `VITE_SERVER_URL` env var (root `.env` → bundled at build time).
 *   4. `ws://localhost:7171` when running on localhost in a regular browser
 *      (default dev port).
 *   5. `wss://<host>/ws` for production deployments behind a reverse proxy.
 */
function resolveServerUrl(): string {
  const fromAndroid = import.meta.env.VITE_SERVER_URL_ANDROID as
    | string
    | undefined;
  const fromDesktop = import.meta.env.VITE_SERVER_URL_DESKTOP as
    | string
    | undefined;
  const fromEnv = import.meta.env.VITE_SERVER_URL as string | undefined;

  if (isCapacitorNative()) {
    // Prefer the Android-specific override; fall back to the shared env var
    // so a single-environment deploy still works. We deliberately do NOT
    // fall through to the `location.hostname === 'localhost'` branch here:
    // inside the APK the WebView always reports `localhost` as its host.
    if (fromAndroid && fromAndroid.length > 0) return fromAndroid;
    if (fromEnv && fromEnv.length > 0) return fromEnv;
    // No env set — assume the operator is testing against an emulator with
    // a dev server on the host machine. `10.0.2.2` is the Android emulator
    // loopback to the host's `localhost`.
    return 'ws://10.0.2.2:7171';
  }

  if (isElectronDesktop()) {
    if (fromDesktop && fromDesktop.length > 0) return fromDesktop;
    if (fromEnv && fromEnv.length > 0) return fromEnv;
    // Same rationale as Android: `location.hostname` under file:// is
    // empty / blob, so we cannot derive a sensible default. Fall back
    // to the public production host so an unconfigured dev still gets
    // a working game.
    return 'wss://game.playzerion.com/ws';
  }

  if (fromEnv && fromEnv.length > 0) return fromEnv;
  if (location.hostname === 'localhost') return 'ws://localhost:7171';
  return `wss://${location.host}/ws`;
}


/**
 * Pull any runtime-registered locales from the server before the UI boots.
 *
 * Languages bundled inside the shared package (en, es) are already loaded.
 * The server also exposes a public `/locales.json` endpoint that lists every
 * locale currently registered — including JSON packs an operator dropped into
 * `data/locales/` or uploaded through the admin console. Merging those here
 * means new languages appear in the game's language picker without having to
 * rebuild or redeploy the client.
 *
 * Failures are non-fatal: the game still works with its built-in languages.
 */
async function hydrateServerLocales(wsUrl: string): Promise<void> {
  const httpBase = wsUrl.replace(/^ws/, 'http').replace(/\/ws$/, '');
  try {
    const listRes = await fetch(`${httpBase}/locales.json`, { cache: 'no-cache' });
    if (!listRes.ok) return;
    const list = await listRes.json() as { locales: Array<{ code: string }> };
    // Locales bundled inside the shared package don't need to be fetched
    // again at runtime — keep this list in sync with `i18n/index.ts`.
    const known = new Set(['en', 'es', 'pt']);
    const extra = list.locales.filter((l) => !known.has(l.code));
    for (const l of extra) {
      try {
        const res = await fetch(`${httpBase}/locales/${encodeURIComponent(l.code)}.json`, { cache: 'no-cache' });
        if (!res.ok) continue;
        const dict = await res.json() as Record<string, string>;
        loadLocale(l.code, dict);
      } catch (err) {
        console.warn(`[i18n] failed to load remote locale ${l.code}:`, err);
      }
    }
  } catch (err) {
    console.warn('[i18n] could not reach server for locale discovery:', err);
  }
}

const game = new ViralSpace();
// Apply the saved locale before any UI renders so the very first frame
// of the auth screen comes up in the player's chosen language. Locales
// added at runtime (via the server's `/locales.json` endpoint) get
// picked up by the same `localStorage` value the next page load.
hydrateServerLocales(resolveServerUrl())
  .finally(() => {
    loadPersistedLocale();
    game.init().catch(console.error);
  });
